import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { BlockSystemCanvas } from '@/app/components/BlockSystemCanvas';
import { BlockSystemSidebar } from '@/app/components/BlockSystemSidebar';
import { SystemPropertiesPanel } from '@/app/components/SystemPropertiesPanel';
import { BackgroundSettingsDialog } from '@/app/components/BackgroundSettings';
import { ViewSettingsDialog } from '@/app/components/ViewSettingsDialog';
import { TextAnnotationDialog } from '@/app/components/TextAnnotationDialog';
import { LabelEditDialog } from '@/app/components/LabelEditDialog';
import { NavigationDrawer } from '@/app/components/NavigationDrawer';
import { PipeCreateDialog } from '@/app/components/PipeCreateDialog';
import { DimensionEditDialog } from '@/app/components/DimensionEditDialog';
import { BlockDisplayOverrideDialog } from '@/app/components/BlockDisplayOverrideDialog';
import { ParameterManagementDialog } from '@/app/components/ParameterManagementDialog';
import { SelectionFilterDropdown } from '@/app/components/SelectionFilterDropdown';
import { TutorialHelpButton } from '@/app/components/tutorial/TutorialHelpButton';
import { Button } from '@/app/components/ui/button';
import { useAutoSave, loadSavedData } from '@/app/hooks/useAutoSave';
import { emitTutorialEvent } from '@/app/contexts/TutorialContext';
import { rotateTransform } from '@/app/utils/transform';
import { isoToScreen } from '@/app/utils/isometric';
import { updateConnectionMarkers, cleanupConnections } from '@/app/utils/connectionMarkers';
import { detectConnections } from '@/app/utils/connectionDetection';
import { resolveLabelData } from '@/app/utils/labelResolver';
import { cleanupCorruptedLocalStorage } from '@/app/utils/cleanupLocalStorage';
import { updateAllLengthAttributes } from '@/app/utils/lengthCalculation';
import { detectSystems } from '@/app/utils/systemDetection';
import { createSubsystemFromSelection, saveSubsystemToLibrary } from '@/app/utils/subsystemCreation';
import { placeSubsystem } from '@/app/utils/subsystemPlacement';
import { 
  loadPrintConfig, 
  savePrintConfig, 
  loadAllPresets, 
  saveCustomPresets 
} from '@/app/utils/printPersistence';
import { PlanManagerPanel } from '@/app/components/plans/PlanManagerPanel';
import { RestrictedFeature } from '@/app/components/RestrictedFeature';
import { PERMISSIONS } from '@/app/constants/plans';
import { PlanLayoutEditor } from '@/app/components/plans/PlanLayoutEditor';
import { SidebarTab } from '@/app/components/BlockSystemSidebar';
import { ResizableLayout } from '@/app/components/ResizableLayoutSimple';
import { 
  Menu, 
  Grid,
  Ruler,
  Cable,
  Trash2,
  Settings,
  Type,
  Magnet,
  Link,
  Database,
  Tag,
  PanelLeft,
  Layers,
  Undo2,
  Redo2,
  Axis3d,
} from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import {
  PlacedBlock,
  SocketConnection,
  BlockSystem,
  ViewState,
  BackgroundSettings,
  ViewDisplaySettings,
  PartAttribute,
  IsomProject,
  IsoPoint,
  PipeType,
  Dimension,
  IsomPart,
  TextAnnotation,
  BlockSystemTool,
  ConnectionMarker,
  LabelTemplate,
  PlacedLabel,
  DrawingFolder,
  Drawing,
  ProjectStructure,
  SystemData,
  PlanSheet,
  Subsystem,
  SelectionFilters,
} from '@/app/types/cad';
import { toast } from 'sonner';
import { useLanguage } from '@/app/contexts/LanguageContext';

// Migration function to update old socket formats to new format
function migrateBlockData(blocks: PlacedBlock[]): PlacedBlock[] {
  return blocks.map(block => {
    const migratedElements = block.blockData.elements.map(element => {
      if (element.type === 'connection') {
        // Check if this is an old-format socket (has 'size' or missing diameter/strokeWidth)
        const oldSocket = element as any;
        
        // If diameter or strokeWidth are missing, add defaults
        if (typeof oldSocket.diameter === 'undefined' || typeof oldSocket.strokeWidth === 'undefined') {
          console.log('🔧 Migrating old socket format:', element.id);
          
          // Remove old 'size' property if it exists
          const { size, ...rest } = oldSocket;
          
          return {
            ...rest,
            type: 'connection' as const,
            diameter: 50, // Default 50mm diameter
            strokeWidth: 5, // Default 5px stroke width
            fillType: oldSocket.fillType || 'ring' as const,
          };
        }
      }
      return element;
    });

    return {
      ...block,
      blockData: {
        ...block.blockData,
        elements: migratedElements,
      },
    };
  });
}

/**
 * Bereinigt ungültige Verbindungen beim initialen Laden (alte Formate ohne position/connectedSockets)
 */
function cleanupInitialConnections(connections: SocketConnection[]): SocketConnection[] {
  const validConnections = connections.filter(conn => {
    // Neue Verbindungen müssen position und connectedSockets haben
    const isValid = conn.position && 
                   conn.position.u !== undefined && 
                   conn.position.v !== undefined &&
                   conn.connectedSockets && 
                   conn.connectedSockets.length >= 2;
    
    if (!isValid) {
      console.log('🗑️ Entferne ungültige Verbindung (altes Format):', conn.id);
    }
    
    return isValid;
  });
  
  if (validConnections.length < connections.length) {
    console.log(`✨ ${connections.length - validConnections.length} ungültige Verbindung(en) entfernt`);
  }
  
  return validConnections;
}

export function BlockSystemEditor({ 
  onSystemsChange,
  onBlocksChange,
  onConnectionsChange,
}: { 
  onSystemsChange?: (systems: SystemData[]) => void;
  onBlocksChange?: (blocks: PlacedBlock[]) => void;
  onConnectionsChange?: (connections: SocketConnection[]) => void;
}) {
  const { t } = useLanguage();
  
  // Cleanup corrupted localStorage entries before loading
  cleanupCorruptedLocalStorage();
  
  // Load saved data on mount
  // OPTIMIZATION: Consolidated localStorage to reduce quota usage
  // - blocksystem-drawing-data: blocks, connections, connectionMarkers, systems, textAnnotations, placedLabels, pipeTypes, labelTemplates
  // - blocksystem-project-meta: projectName, selectedPipeTypeId, rootFolder, activeDrawingId
  // - blocksystem-plans-data: plans, titleBlockTemplates, activePlanId
  // - blocksystem-view-settings: viewState, backgroundSettings, viewDisplaySettings, showDimensions, snapEnabled
  // Falls back to old individual keys for backward compatibility
  const drawingData = loadSavedData<any>('blocksystem-drawing-data', null);
  
  const initialBlocks = updateAllLengthAttributes(
    migrateBlockData(
      drawingData?.blocks || loadSavedData<PlacedBlock[]>('blocksystem-blocks', [])
    ),
    loadSavedData<BackgroundSettings>('blocksystem-backgroundsettings', { isometricGridSize: 200 } as any).isometricGridSize
  );
  const initialConnections = cleanupInitialConnections(
    drawingData?.connections || loadSavedData<SocketConnection[]>('blocksystem-connections', [])
  );
  const initialSystems = drawingData?.systems || loadSavedData<BlockSystem[]>('blocksystem-systems', []);
  
  const projectMeta = loadSavedData<any>('blocksystem-project-meta', null);
  const initialProjectName = projectMeta?.projectName || loadSavedData<string>('blocksystem-projectname', t('common.newPart'));
  
  const [blocks, setBlocks] = useState<PlacedBlock[]>(initialBlocks);
  const [connections, setConnections] = useState<SocketConnection[]>(initialConnections);
  const [connectionMarkers, setConnectionMarkers] = useState<ConnectionMarker[]>(
    drawingData?.connectionMarkers || loadSavedData<ConnectionMarker[]>('blocksystem-connectionmarkers', [])
  );
  const [systems, setSystems] = useState<BlockSystem[]>(initialSystems);
  
  // Plan Management State
  const plansData = loadSavedData<any>('blocksystem-plans-data', null);
  const [plans, setPlans] = useState<PlanSheet[]>(
    plansData?.plans || loadSavedData<PlanSheet[]>('blocksystem-plans', [])
  );
  const [titleBlockTemplates, setTitleBlockTemplates] = useState<TitleBlockTemplate[]>(
    plansData?.titleBlockTemplates || loadSavedData<TitleBlockTemplate[]>('blocksystem-titleblocktemplates', [])
  );
  const [activePlanId, setActivePlanId] = useState<string | undefined>(
    plansData?.activePlanId || loadSavedData<string>('blocksystem-activeplanid', '') || undefined
  );
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('browser');
  
  // Refs to track latest state values (avoid stale closures)
  const blocksRef = useRef(blocks);
  const connectionsRef = useRef(connections);
  const systemsRef = useRef(systems);
  const [textAnnotations, setTextAnnotations] = useState<TextAnnotation[]>(
    drawingData?.textAnnotations || loadSavedData<TextAnnotation[]>('blocksystem-textannotations', [])
  );
  const [labelTemplates, setLabelTemplates] = useState<LabelTemplate[]>(
    drawingData?.labelTemplates || loadSavedData<LabelTemplate[]>('blocksystem-labeltemplates', [])
  );
  const [placedLabels, setPlacedLabels] = useState<PlacedLabel[]>(
    drawingData?.placedLabels || loadSavedData<PlacedLabel[]>('blocksystem-placedlabels', [])
  );
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);
  const [selectedTextIds, setSelectedTextIds] = useState<string[]>([]);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState<string[]>([]);
  const [selectedMarkerIds, setSelectedMarkerIds] = useState<string[]>([]);
  const [selectedLabelTemplateId, setSelectedLabelTemplateId] = useState<string>();
  const [projectName, setProjectName] = useState(initialProjectName);

  const viewSettings = loadSavedData<any>('blocksystem-view-settings', null);
  
  const [viewState, setViewState] = useState<ViewState>(
    viewSettings?.viewState || loadSavedData<ViewState>('blocksystem-viewstate', {
      zoom: 1,
      panX: 400,
      panY: 300,
    })
  );

  // View Display Settings - must be declared before useAutoSave
  const [viewDisplaySettings, setViewDisplaySettings] = useState<ViewDisplaySettings>(
    viewSettings?.viewDisplaySettings || loadSavedData<ViewDisplaySettings>('blocksystem-viewdisplaysettings', {
      lineWidthMultiplier: 1.0,
      connectionSizeMultiplier: 1.0,
      fontSizeMultiplier: 1.0,
      iconSizeMultiplier: 1.0,
      showLabels: true,
    })
  );

  const [backgroundSettings, setBackgroundSettings] = useState<BackgroundSettings>(
    viewSettings?.backgroundSettings || loadSavedData<BackgroundSettings>('blocksystem-backgroundsettings', {
      mode: 'isometric',
      visible: true,
      isometricAngle: 30,
      isometricGridSize: 200,
      isometricLineColor: '#cccccc',
      isometricLineWidth: 1,
      isometricPointColor: '#666666',
      isometricPointSize: 1.5,
      cartesianGridSize: 200,
      cartesianLineColor: '#cccccc',
      cartesianLineWidth: 1,
      cartesianShowMinorLines: true,
      cartesianMinorDivisions: 5,
      cartesianMinorLineColor: '#e0e0e0',
      dotsGridSize: 200,
      dotsColor: '#999999',
      dotsSize: 2,
      backgroundColor: '#ffffff',
    })
  );

  // UI State
  const [showBackgroundSettings, setShowBackgroundSettings] = useState(false);
  const [showViewSettings, setShowViewSettings] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [previewBackgroundSettings, setPreviewBackgroundSettings] = useState<BackgroundSettings>(backgroundSettings);
  const [showDimensions, setShowDimensions] = useState(
    viewSettings?.showDimensions ?? loadSavedData<boolean>('blocksystem-showdimensions', true)
  );
  
  // Snap-to-Grid State
  const [snapEnabled, setSnapEnabled] = useState(
    viewSettings?.snapEnabled ?? loadSavedData<boolean>('blocksystem-snapenabled', true)
  );
  
  // Flag to temporarily disable auto-connection detection (e.g., after paste)
  const [skipConnectionDetection, setSkipConnectionDetection] = useState(false);
  
  // Clipboard state
  interface ClipboardData {
    blocks: PlacedBlock[];
    connections: SocketConnection[];
    textAnnotations: TextAnnotation[];
    placedLabels: PlacedLabel[];
    systemInfo?: {
      name: string;
      color: string;
      attributes?: PartAttribute[];
      description?: string;
    };
    // NEW: For copying connection attributes only (without structure)
    connectionAttributes?: PartAttribute[];
    sourceConnectionCount?: number; // Track how many connections were copied from
  }
  const [clipboard, setClipboard] = useState<ClipboardData>({ 
    blocks: [], 
    connections: [], 
    textAnnotations: [], 
    placedLabels: [] 
  });

  // Track mouse position for paste operations
  const mousePositionRef = useRef<IsoPoint | null>(null);

  // Dimension edit dialog state
  const [showDimensionDialog, setShowDimensionDialog] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState<{ blockId: string; dimension: Dimension } | null>(null);

  // Tool state
  const [activeTool, setActiveTool] = useState<BlockSystemTool>('select');
  const [pipeDrawStart, setPipeDrawStart] = useState<IsoPoint | null>(null);
  const [pipeDrawPreview, setPipeDrawPreview] = useState<IsoPoint | null>(null);
  const [showPipeDialog, setShowPipeDialog] = useState(false);
  
  // Selection filter state
  const [filterConnectionsOnly, setFilterConnectionsOnly] = useState(false);
  const [selectionFilters, setSelectionFilters] = useState<SelectionFilters>({
    blocks: true,
    lines: true,
    circles: true,
    arcs: true,
    shapes: true,
    splines: true,
    sockets: true,
    connections: true,
    labels: true,
    texts: true,
    dimensions: true,
    subsystems: true,
  });
  
  // Coordinate system visibility
  const [showCoordinateSystem, setShowCoordinateSystem] = useState(true);
  
  // Text annotation state
  const [showTextDialog, setShowTextDialog] = useState(false);
  const [editingTextAnnotation, setEditingTextAnnotation] = useState<TextAnnotation | null>(null);
  const [textPlacementPosition, setTextPlacementPosition] = useState<IsoPoint | null>(null);

  // Label edit state
  const [showLabelEditDialog, setShowLabelEditDialog] = useState(false);
  const [editingLabel, setEditingLabel] = useState<PlacedLabel | null>(null);
  
  // Label reassign state - NEW: For reassigning orphaned labels to new target elements
  const [reassigningLabelId, setReassigningLabelId] = useState<string | null>(null);

  // Block display override state
  const [showBlockDisplayDialog, setShowBlockDisplayDialog] = useState(false);
  const [blockDisplayOverrideId, setBlockDisplayOverrideId] = useState<string | null>(null);

  // Parameter management dialog state
  const [showParameterManagement, setShowParameterManagement] = useState(false);

  // Drawing Management State - load from localStorage
  const [rootFolder, setRootFolder] = useState<DrawingFolder>(() => {
    const saved = projectMeta?.rootFolder || loadSavedData<DrawingFolder>('blocksystem-drawingfolder', null);
    if (saved && saved.children && saved.children.length > 0) {
      return saved;
    }
    return {
      id: 'root',
      name: 'Root',
      children: [],
      expanded: true,
    };
  });
  
  const [activeDrawingId, setActiveDrawingId] = useState<string | undefined>(() => {
    const saved = projectMeta?.activeDrawingId || loadSavedData<string>('blocksystem-activedrawingid', '');
    return saved || undefined;
  });

  // Undo/Redo History
  interface HistoryState {
    blocks: PlacedBlock[];
    connections: SocketConnection[];
    systems: BlockSystem[];
    textAnnotations: TextAnnotation[];
    placedLabels: PlacedLabel[];
  }
  
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoRef = useRef(false);
  
  // Save state to history
  const saveToHistory = useCallback(() => {
    // Don't save to history if we're currently doing undo/redo
    if (isUndoRedoRef.current) {
      return;
    }

    const newState: HistoryState = {
      blocks: JSON.parse(JSON.stringify(blocks)),
      connections: JSON.parse(JSON.stringify(connections)),
      systems: JSON.parse(JSON.stringify(systems)),
      textAnnotations: JSON.parse(JSON.stringify(textAnnotations)),
      placedLabels: JSON.parse(JSON.stringify(placedLabels))
    };
    
    setHistory((prev) => {
      // Remove any future states if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      
      // Add new state
      newHistory.push(newState);
      
      // Limit history size to 50 entries
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    
    setHistoryIndex((prev) => {
      const newIndex = prev + 1;
      return newIndex >= 50 ? 49 : newIndex;
    });
  }, [blocks, connections, systems, textAnnotations, placedLabels, historyIndex]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoRef.current = true;
      const prevState = history[historyIndex - 1];
      setBlocks(prevState.blocks);
      setConnections(prevState.connections);
      setSystems(prevState.systems);
      setTextAnnotations(prevState.textAnnotations);
      setPlacedLabels(prevState.placedLabels);
      setHistoryIndex((prev) => prev - 1);
      toast.success('Rückgängig');
      
      // Reset flag after state updates
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoRef.current = true;
      const nextState = history[historyIndex + 1];
      setBlocks(nextState.blocks);
      setConnections(nextState.connections);
      setSystems(nextState.systems);
      setTextAnnotations(nextState.textAnnotations);
      setPlacedLabels(nextState.placedLabels);
      setHistoryIndex((prev) => prev + 1);
      toast.success('Wiederholen');
      
      // Reset flag after state updates
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Update refs when states change
  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);
  
  useEffect(() => {
    connectionsRef.current = connections;
  }, [connections]);
  
  useEffect(() => {
    systemsRef.current = systems;
  }, [systems]);

  // Auto-save to history when state changes (with debouncing)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only save if we have actual content or if history is empty
      if (blocks.length > 0 || connections.length > 0 || textAnnotations.length > 0 || placedLabels.length > 0 || history.length === 0) {
        saveToHistory();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [blocks, connections, systems, textAnnotations, placedLabels]); // Don't include saveToHistory to avoid infinite loop

  // Pipe drawing settings
  const [pipeDN, setPipeDN] = useState(100);
  const [pipeColor, setPipeColor] = useState('#000000');
  const [pipeLineWidth, setPipeLineWidth] = useState(2);
  const [pipeLineStyle, setPipeLineStyle] = useState<'solid' | 'dashed' | 'dotted' | 'dashdot'>('solid');

  // Pipe type library (like AutoCAD layers)
  const [pipeTypes, setPipeTypes] = useState<PipeType[]>(
    drawingData?.pipeTypes || loadSavedData<PipeType[]>('blocksystem-pipetypes', [
      {
        id: 'default',
        name: 'Standard DN100',
        dn: 100,
        color: '#000000',
        lineWidth: 2,
        lineStyle: 'solid',
      },
    ])
  );
  const [selectedPipeTypeId, setSelectedPipeTypeId] = useState<string>(
    projectMeta?.selectedPipeTypeId || 'default'
  );

  // Load settings from selected pipe type
  useEffect(() => {
    const selected = pipeTypes.find((t) => t.id === selectedPipeTypeId);
    if (selected) {
      setPipeDN(selected.dn);
      setPipeColor(selected.color);
      setPipeLineWidth(selected.lineWidth);
      setPipeLineStyle(selected.lineStyle);
    }
  }, [selectedPipeTypeId, pipeTypes]);

  // Initialize drawing browser - load saved structure or create new
  useEffect(() => {
    // Helper to find a drawing by ID
    const findDrawingById = (folder: DrawingFolder, id: string): Drawing | null => {
      for (const child of folder.children) {
        if ('blocks' in child && child.id === id) return child;
        if ('children' in child) {
          const found = findDrawingById(child, id);
          if (found) return found;
        }
      }
      return null;
    };

    // Find any drawing in the folder structure recursively (fallback)
    const findFirstDrawing = (folder: DrawingFolder): Drawing | null => {
      for (const child of folder.children) {
        if ('blocks' in child) return child;
        if ('children' in child) {
          const found = findFirstDrawing(child);
          if (found) return found;
        }
      }
      return null;
    };
    
    // Determine which drawing to load
    let drawingToLoad: Drawing | null = null;
    
    if (activeDrawingId) {
      drawingToLoad = findDrawingById(rootFolder, activeDrawingId);
    }
    
    // Fallback to first drawing if active not found
    if (!drawingToLoad) {
      drawingToLoad = findFirstDrawing(rootFolder);
    }
    
    if (drawingToLoad) {
      // We have saved drawings - load the selected one
      console.log('📂 Loading saved drawing structure with', rootFolder.children.length, 'items');
      console.log('📄 Loading drawing:', drawingToLoad.name);
      
      setBlocks(drawingToLoad.blocks || []);
      setConnections(drawingToLoad.connections || []);
      setSystems(drawingToLoad.systems || []);
      setTextAnnotations(drawingToLoad.textAnnotations || []);
      setLabelTemplates(drawingToLoad.labelTemplates || []);
      setPlacedLabels(drawingToLoad.placedLabels || []);
      if (drawingToLoad.backgroundSettings) setBackgroundSettings(drawingToLoad.backgroundSettings);
      if (drawingToLoad.viewDisplaySettings) setViewDisplaySettings(drawingToLoad.viewDisplaySettings);
      if (drawingToLoad.viewState) setViewState(drawingToLoad.viewState);
      if (drawingToLoad.pipeTypes) setPipeTypes(drawingToLoad.pipeTypes);
      setPlans(drawingToLoad.plans || []);
      
      if (!activeDrawingId || activeDrawingId !== drawingToLoad.id) {
        setActiveDrawingId(drawingToLoad.id);
      }
    } else {
      // No saved drawings - create initial one
      console.log('📝 No saved drawings found, creating initial drawing');
      const initialDrawing: Drawing = {
        id: `drawing-${Date.now()}`,
        name: 'Aktuelle Zeichnung',
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        blocks: [],
        connections: [],
        systems: [],
        textAnnotations: [],
        labelTemplates: [],
        placedLabels: [],
        backgroundSettings: backgroundSettings,
        viewDisplaySettings: viewDisplaySettings,
        viewState: viewState,
        pipeTypes: pipeTypes,
      };
      
      setRootFolder({
        id: 'root',
        name: 'Root',
        children: [initialDrawing],
        expanded: true,
      });
      setActiveDrawingId(initialDrawing.id);
    }
  }, []);

  // Auto-update active drawing when state changes - DISABLED to prevent conflicts with loading
  // The drawing is saved via useAutoSave instead
  /*
  useEffect(() => {
    if (!activeDrawingId) return;
    
    const updateDrawing = (folder: DrawingFolder): DrawingFolder => {
      return {
        ...folder,
        children: folder.children.map((child) => {
          if ('blocks' in child && child.id === activeDrawingId) {
            return {
              ...child,
              blocks,
              connections,
              systems,
              textAnnotations,
              labelTemplates,
              placedLabels,
              backgroundSettings,
              viewDisplaySettings,
              viewState,
              pipeTypes,
              modified: new Date().toISOString(),
            };
          } else if ('children' in child) {
            return updateDrawing(child);
          }
          return child;
        }),
      };
    };
    
    setRootFolder((prev) => updateDrawing(prev));
  }, [activeDrawingId, blocks, connections, systems, textAnnotations, labelTemplates, placedLabels, backgroundSettings, viewDisplaySettings, viewState, pipeTypes]);
  */

  // Handle save current settings as new pipe type
  const handleSavePipeType = useCallback(() => {
    const name = prompt('Name für den Leitungstyp:', `DN${pipeDN} ${pipeColor}`);
    if (!name) return;

    const newType: PipeType = {
      id: `type-${Date.now()}`,
      name,
      dn: pipeDN,
      color: pipeColor,
      lineWidth: pipeLineWidth,
      lineStyle: pipeLineStyle,
    };

    setPipeTypes((prev) => [...prev, newType]);
    setSelectedPipeTypeId(newType.id);
    toast.success(t('editor.status.saved'));
  }, [pipeDN, pipeColor, pipeLineWidth, pipeLineStyle]);

  // Handle delete pipe type
  const handleDeletePipeType = useCallback(() => {
    if (selectedPipeTypeId === 'default') {
      toast.error('Standard-Typ kann nicht gelöscht werden');
      return;
    }

    const selected = pipeTypes.find((t) => t.id === selectedPipeTypeId);
    if (!selected) return;

    if (!window.confirm(`Leitungstyp "${selected.name}" wirklich löschen?`)) return;

    setPipeTypes((prev) => prev.filter((t) => t.id !== selectedPipeTypeId));
    setSelectedPipeTypeId('default');
    toast.success(`Leitungstyp "${selected.name}" gelöscht`);
  }, [selectedPipeTypeId, pipeTypes]);

  // Handle rename pipe type
  const handleRenamePipeType = useCallback(() => {
    if (selectedPipeTypeId === 'default') {
      toast.error('Standard-Typ kann nicht umbenannt werden');
      return;
    }

    const selected = pipeTypes.find((t) => t.id === selectedPipeTypeId);
    if (!selected) return;

    const newName = prompt('Neuer Name für den Leitungstyp:', selected.name);
    if (!newName || newName.trim() === '') return;

    setPipeTypes((prev) =>
      prev.map((t) => (t.id === selectedPipeTypeId ? { ...t, name: newName.trim() } : t))
    );
    toast.success(`Leitungstyp umbenannt zu "${newName.trim()}"`);
  }, [selectedPipeTypeId, pipeTypes]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Automatically update length attributes for all line components
  const blocksSignature = useMemo(() => {
    return blocks.map(b => {
      const lineElements = b.blockData.elements.filter(el => el.type === 'line');
      if (lineElements.length === 0) return `${b.id}-no-lines`;
      
      // Create signature from line endpoints (positions)
      const linesSignature = lineElements.map(el => {
        if (el.type === 'line') {
          return `${el.start.x},${el.start.y},${el.start.z}-${el.end.x},${el.end.y},${el.end.z}`;
        }
        return '';
      }).join('|');
      
      return `${b.id}-${linesSignature}`;
    }).join(';');
  }, [blocks]);

  useEffect(() => {
    const updatedBlocks = updateAllLengthAttributes(blocks, backgroundSettings.isometricGridSize);
    
    // Only update state if something actually changed to avoid infinite loop
    const hasChanges = updatedBlocks.some((block, idx) => {
      const oldBlock = blocks[idx];
      if (!oldBlock) return true;
      
      const oldLength = oldBlock.blockData.attributes?.find(a => a.name === 'Länge')?.value;
      const newLength = block.blockData.attributes?.find(a => a.name === 'Länge')?.value;
      
      return oldLength !== newLength;
    });
    
    if (hasChanges) {
      console.log('🔢 Auto-updating length attributes for line components (gridSize:', backgroundSettings.isometricGridSize, 'mm)');
      setBlocks(updatedBlocks);
    }
  }, [blocksSignature, backgroundSettings.isometricGridSize]); // Re-run when line positions or grid size change

  // Auto-save state to localStorage
  // NOTE: useAutoSave has been updated to include unmount protection
  // CONSOLIDATED: Save related state together to reduce localStorage overhead
  
  // Critical drawing data (most important)
  useAutoSave('blocksystem-drawing-data', {
    blocks,
    connections,
    connectionMarkers,
    systems,
    textAnnotations,
    placedLabels,
    pipeTypes,
    labelTemplates,
  }, 3000); // 3 seconds
  
  // Project metadata
  useAutoSave('blocksystem-project-meta', {
    projectName,
    selectedPipeTypeId,
    rootFolder,
    activeDrawingId: activeDrawingId || '',
  }, 4000); // 4 seconds
  
  // Plans and templates
  useAutoSave('blocksystem-plans-data', {
    plans,
    titleBlockTemplates,
    activePlanId: activePlanId || '',
  }, 5000); // 5 seconds
  
  // UI/View settings (less critical, can be lost)
  useAutoSave('blocksystem-view-settings', {
    viewState,
    backgroundSettings,
    viewDisplaySettings,
    showDimensions,
    snapEnabled,
  }, 6000); // 6 seconds - Longer interval for UI settings

  // SIMPLE SYSTEM SYNC - Run once on mount to detect systems from localStorage
  const hasDetectedSystemsOnMount = useRef(false);
  useEffect(() => {
    if (!hasDetectedSystemsOnMount.current && blocks.length > 0) {
      hasDetectedSystemsOnMount.current = true;
      
      console.log('🔍 Detecting systems on mount...');
      const result = detectSystems(blocks, connections, systems);
      
      setBlocks(result.blocks);
      setSystems(result.systems);
    }
  }, []); // Empty deps - run ONCE on mount

  // Propagate systems to parent component
  useEffect(() => {
    if (onSystemsChange) {
      // Convert BlockSystem to SystemData format
      const systemsData: SystemData[] = systems.map(sys => {
        // Convert PartAttribute[] to simple object
        const attributesObj: { [key: string]: string | number } = {};
        if (sys.attributes) {
          sys.attributes.forEach(attr => {
            attributesObj[attr.name] = attr.value;
          });
        }
        
        return {
          id: sys.id,
          name: sys.name,
          color: sys.color,
          blocks: sys.blockIds || [],
          connections: sys.connectionIds || [],
          attributes: attributesObj,
          description: sys.description,
        };
      });
      onSystemsChange(systemsData);
    }
  }, [systems, onSystemsChange]);

  // Propagate blocks to parent component
  useEffect(() => {
    if (onBlocksChange) {
      onBlocksChange(blocks);
    }
  }, [blocks, onBlocksChange]);

  // Propagate connections to parent component
  useEffect(() => {
    if (onConnectionsChange) {
      onConnectionsChange(connections);
    }
  }, [connections, onConnectionsChange]);

  // Automatically update connection markers when connections or blocks change
  useEffect(() => {
    // First, cleanup invalid connections
    const result = cleanupConnections(connections, blocks);
    
    // Safety check for the result
    if (!result || !result.validConnections || !result.removedConnectionIds) {
      console.error('❌ cleanupConnections returned invalid result:', result);
      return;
    }
    
    const { validConnections, removedConnectionIds } = result;
    
    // If any connections were removed, update the state
    if (removedConnectionIds.length > 0) {
      console.log('🧹 Removed invalid connections:', removedConnectionIds);
      setConnections(validConnections);
      
      // ⚠️ LABELS ARE NOW 100% PERSISTENT - NEVER AUTO-DELETE!
      // Labels remain even if their target connection is deleted
      // User must manually delete labels they don't want
      console.log('ℹ️ Keeping all labels (persistent mode - no auto-delete)');
      
      toast.info(`${removedConnectionIds.length} ungültige Verbindung(en) entfernt`);
    }
    
    // Update markers for all valid connections
    const newMarkers = updateConnectionMarkers(validConnections, blocks, connectionMarkers);
    
    // Only update if markers changed (compare by serialization to avoid unnecessary updates)
    const newMarkersJson = JSON.stringify(newMarkers);
    const oldMarkersJson = JSON.stringify(connectionMarkers);
    if (newMarkersJson !== oldMarkersJson) {
      console.log('🔄 Updated connection markers:', newMarkers.length);
      setConnectionMarkers(newMarkers);
    }
  }, [connections, blocks, connectionMarkers]);

  // Automatische Verbindungserkennung bei Block-Änderungen
  useEffect(() => {
    // Skip auto-detection if flag is set (e.g., right after paste)
    if (skipConnectionDetection) {
      console.log('🔍 Verbindungserkennung übersprungen (nach Paste)');
      return;
    }
    
    console.log('🔍 Starte automatische Verbindungserkennung...');
    // Erhöhe die Toleranz auf 0.5 Grid-Einheiten für bessere Erkennung
    const detectedConnections = detectConnections(blocks, systems, 0.5, connections);
    
    // Vergleiche mit bestehenden Verbindungen (um unnötige Updates zu vermeiden)
    const currentConnectionsJson = JSON.stringify(
      connections.map(c => ({ position: c.position, sockets: c.connectedSockets })).sort()
    );
    const detectedConnectionsJson = JSON.stringify(
      detectedConnections.map(c => ({ position: c.position, sockets: c.connectedSockets })).sort()
    );
    
    if (currentConnectionsJson !== detectedConnectionsJson) {
      console.log('✨ Verbindungen haben sich geändert - Update...');
      console.log('📋 Erkannte Verbindungen:', detectedConnections.map(c => ({
        id: c.id,
        position: c.position,
        socketCount: c.connectedSockets.length,
        systemId: c.systemId
      })));
      setConnections(detectedConnections);
    }
  }, [blocks, systems, skipConnectionDetection]); // Läuft bei Block- oder System-Änderungen

  // Clean up invalid labels (labels referencing non-existent elements)
  // NOTE: placedLabels is NOT in dependencies to prevent immediate cleanup after paste
  // (labels need time to be remapped after auto-detection creates connections)
  useEffect(() => {
    const validBlockIds = new Set(blocks.map(b => b.id));
    const validConnectionIds = new Set(connections.map(c => c.id));
    const validTextIds = new Set(textAnnotations.map(t => t.id));
    
    const invalidLabels = placedLabels.filter(label => {
      if (label.targetElementType === 'block' && !validBlockIds.has(label.targetElementId)) {
        return true;
      }
      if (label.targetElementType === 'connection' && !validConnectionIds.has(label.targetElementId)) {
        return true;
      }
      if (label.targetElementType === 'text-annotation' && !validTextIds.has(label.targetElementId)) {
        return true;
      }
      return false;
    });
    
    if (invalidLabels.length > 0) {
      console.warn('🧹 Cleaning up', invalidLabels.length, 'invalid labels:', invalidLabels.map(l => ({
        id: l.id,
        targetType: l.targetElementType,
        targetId: l.targetElementId
      })));
      
      setPlacedLabels(prev => prev.filter(label => {
        if (label.targetElementType === 'block') return validBlockIds.has(label.targetElementId);
        if (label.targetElementType === 'connection') return validConnectionIds.has(label.targetElementId);
        if (label.targetElementType === 'text-annotation') return validTextIds.has(label.targetElementId);
        return true;
      }));
    }
  }, [blocks, connections, textAnnotations]); // placedLabels REMOVED to prevent race condition during paste

  // Stabilize backgroundSettings reference
  const stableBackgroundSettings = React.useRef(backgroundSettings);
  if (JSON.stringify(stableBackgroundSettings.current) !== JSON.stringify(backgroundSettings)) {
    stableBackgroundSettings.current = backgroundSettings;
  }

  const stablePreviewSettings = React.useRef(previewBackgroundSettings);
  if (JSON.stringify(stablePreviewSettings.current) !== JSON.stringify(previewBackgroundSettings)) {
    stablePreviewSettings.current = previewBackgroundSettings;
  }

  const activeBackgroundSettings = React.useMemo(
    () => (showBackgroundSettings ? stablePreviewSettings.current : stableBackgroundSettings.current),
    [showBackgroundSettings, stableBackgroundSettings.current, stablePreviewSettings.current]
  );

  // System colors map
  const systemColors = React.useMemo(() => {
    const map = new Map<string, string>();
    systems.forEach((system) => {
      map.set(system.id, system.color);
    });
    console.log('🎨 System colors map:', Array.from(map.entries()));
    return map;
  }, [systems]);

  // Selected system (if all selected blocks belong to same system)
  const selectedSystem = React.useMemo(() => {
    if (selectedBlockIds.length === 0) return null;

    const selectedBlocks = blocks.filter((b) => selectedBlockIds.includes(b.id));
    const systemIds = new Set(selectedBlocks.map((b) => b.systemId).filter((id): id is string => id !== undefined));

    if (systemIds.size === 1) {
      const systemId = Array.from(systemIds)[0];
      return systems.find((s) => s.id === systemId) || null;
    }

    return null;
  }, [selectedBlockIds, blocks, systems]);

  // Helper: Add block to parts library automatically
  const addBlockToLibrary = useCallback((block: PlacedBlock) => {
    try {
      const stored = localStorage.getItem('isompart-library');
      const currentLibrary: IsomPart[] = stored ? JSON.parse(stored) : [];
      
      // Check if this part already exists in library
      const existsInLibrary = currentLibrary.some(libPart => 
        libPart.name === block.blockData.name && 
        libPart.created === block.blockData.created
      );
      
      if (!existsInLibrary) {
        // Ensure the block has an ID
        const partToAdd = {
          ...block.blockData,
          id: block.blockData.id || `part-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
        
        const updatedLibrary = [...currentLibrary, partToAdd];
        localStorage.setItem('isompart-library', JSON.stringify(updatedLibrary));
        
        // Trigger library update event so BlockLibraryPanel reloads
        window.dispatchEvent(new CustomEvent('isompart-library-updated'));
        
        console.log(`📚 Auto-added "${block.blockData.name}" to parts library`);
      }
    } catch (error) {
      console.error('❌ Error adding block to library:', error);
    }
  }, []);

  // Handle block add
  const handleBlockAdd = useCallback(
    (block: PlacedBlock) => {
      setBlocks((prev) => [...prev, block]);
      toast.success(`Block "${block.blockData.name}" platziert`);
      
      // Tutorial-Event emittieren
      emitTutorialEvent({ type: 'action-performed', action: 'block-placed' });
      
      // Add block to library automatically
      addBlockToLibrary(block);
    },
    [addBlockToLibrary]
  );

  // Handle block add with connection - ATOMIC operation
  const handleBlockAddWithConnection = useCallback(
    (block: PlacedBlock, connection: SocketConnection) => {
      // Get fresh values from refs (avoid stale closures)
      const currentBlocks = blocksRef.current;
      const currentConnections = connectionsRef.current;
      const currentSystems = systemsRef.current;
      
      const newBlocks = [...currentBlocks, block];
      const newConnections = [...currentConnections, connection];
      
      const result = detectSystems(newBlocks, newConnections, currentSystems);
      
      setBlocks(result.blocks);
      setConnections(newConnections);
      setSystems(result.systems);
      
      toast.success('Verbindung erstellt');
      
      // Add block to library automatically
      addBlockToLibrary(block);
    },
    [addBlockToLibrary]
  );

  // Handle block update
  const handleBlockUpdate = useCallback((id: string, updates: Partial<PlacedBlock>) => {
    // Check if position changed to move associated labels
    if (updates.transform?.position) {
      setBlocks((prev) => {
        const oldBlock = prev.find((b) => b.id === id);
        if (oldBlock) {
          const oldPos = oldBlock.transform.position;
          const newPos = updates.transform!.position;
          const deltaU = newPos.u - oldPos.u;
          const deltaV = newPos.v - oldPos.v;
          
          // Move labels that are attached to this block
          if (deltaU !== 0 || deltaV !== 0) {
            setPlacedLabels((prevLabels) =>
              prevLabels.map((label) => {
                if (label.targetElementType === 'block' && label.targetElementId === id) {
                  return {
                    ...label,
                    position: {
                      u: label.position.u + deltaU,
                      v: label.position.v + deltaV,
                    },
                  };
                }
                return label;
              })
            );
          }
        }
        return prev.map((b) => (b.id === id ? { ...b, ...updates } : b));
      });
    } else {
      setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
    }
  }, []);

  // Handle block context menu (right-click)
  const handleBlockContextMenu = useCallback((blockId: string, x: number, y: number) => {
    setBlockDisplayOverrideId(blockId);
    setShowBlockDisplayDialog(true);
  }, []);

  // Handle block select
  const handleBlockSelect = useCallback((ids: string[]) => {
    // NEW: If in reassign mode, reassign label to selected block
    if (reassigningLabelId && ids.length === 1) {
      const blockId = ids[0];
      const block = blocks.find(b => b.id === blockId);
      
      if (block) {
        // Update label directly to avoid circular dependency
        setPlacedLabels((prev) =>
          prev.map((l) => {
            if (l.id !== reassigningLabelId) return l;
            return {
              ...l,
              targetElementType: 'block' as const,
              targetElementId: blockId,
            };
          })
        );
        
        toast.success(`Label zu "${block.blockData.name}" neu zugewiesen`);
        setReassigningLabelId(null);
        console.log('✅ Label reassigned to block:', blockId);
        return;
      }
    }
    
    setSelectedBlockIds(ids);
  }, [reassigningLabelId, blocks]);

  // Handle connection select
  const handleConnectionSelect = useCallback((ids: string[]) => {
    console.log('🔵 handleConnectionSelect called with IDs:', ids);
    console.log('🔵 Current connections available:', connections.map(c => c.id));
    console.log('🔵 Mismatched IDs:', ids.filter(id => !connections.find(c => c.id === id)));
    
    // NEW: If in reassign mode, reassign label to selected connection
    if (reassigningLabelId && ids.length === 1) {
      const connectionId = ids[0];
      const connection = connections.find(c => c.id === connectionId);
      
      if (connection) {
        // Update label directly to avoid circular dependency
        setPlacedLabels((prev) =>
          prev.map((l) => {
            if (l.id !== reassigningLabelId) return l;
            return {
              ...l,
              targetElementType: 'connection' as const,
              targetElementId: connectionId,
            };
          })
        );
        
        toast.success(`Label zu Verbindung neu zugewiesen`);
        setReassigningLabelId(null);
        console.log('✅ Label reassigned to connection:', connectionId);
        return;
      }
    }
    
    setSelectedConnectionIds(ids);
  }, [connections, reassigningLabelId]);

  // Handle dimension click
  const handleDimensionClick = useCallback((blockId: string, dimensionId: string) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;

    const dimension = block.blockData.elements.find(el => el.id === dimensionId && el.type === 'dimension');
    if (!dimension || dimension.type !== 'dimension') return;

    setSelectedDimension({ blockId, dimension });
    setShowDimensionDialog(true);
  }, [blocks]);

  // Handle dimension update (actualValue)
  const handleDimensionUpdate = useCallback((blockId: string, dimensionId: string, actualValue: string) => {
    setBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== blockId) return block;

        return {
          ...block,
          blockData: {
            ...block.blockData,
            elements: block.blockData.elements.map((el) => {
              if (el.id === dimensionId && el.type === 'dimension') {
                return {
                  ...el,
                  actualValue: actualValue || undefined, // Clear if empty string
                };
              }
              return el;
            }),
          },
        };
      })
    );

    toast.success('Bemaßung aktualisiert');
  }, []);

  // Handle connection create
  const handleConnectionCreateInternal = useCallback(
    (connection: SocketConnection) => {
      console.log('🔗 Creating connection:', connection.id);
      
      // Tutorial-Event emittieren
      emitTutorialEvent({ type: 'action-performed', action: 'pipe-drawn' });
      
      // Get fresh values from refs (avoid stale closures)
      const currentBlocks = blocksRef.current;
      const currentConnections = connectionsRef.current;
      const currentSystems = systemsRef.current;
      
      const newConnections = [...currentConnections, connection];
      
      console.log('🔍 Detecting systems with:', {
        blocks: currentBlocks.length,
        connections: newConnections.length,
        systems: currentSystems.length
      });
      
      console.log('🔍 DEBUG: CONNECTION BEFORE detectSystems:', {
        id: connection.id,
        hasAttributes: !!connection.attributes,
        attributesCount: connection.attributes?.length || 0,
        attributes: JSON.stringify(connection.attributes)
      });
      
      const result = detectSystems(currentBlocks, newConnections, currentSystems);
      
      console.log('✅ System detection result:', {
        blocks: result.blocks.length,
        systems: result.systems.length
      });
      
      // Verify the connection is still intact
      const connectionInArray = newConnections.find(c => c.id === connection.id);
      console.log('🔍 DEBUG: SAME CONNECTION AFTER detectSystems:', {
        id: connectionInArray?.id,
        hasAttributes: !!connectionInArray?.attributes,
        attributesCount: connectionInArray?.attributes?.length || 0,
        attributes: JSON.stringify(connectionInArray?.attributes),
        sameReference: connectionInArray === connection
      });
      
      setBlocks(result.blocks);
      setConnections(newConnections);
      setSystems(result.systems);
      
      toast.success('Verbindung erstellt');
    },
    []
  );

  // Handle system update
  const handleSystemUpdate = useCallback((id: string, updates: Partial<BlockSystem>) => {
    setSystems((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  }, []);

  // Handle text annotation add/update
  const handleTextAnnotationSave = useCallback((updates: Partial<TextAnnotation>) => {
    if (editingTextAnnotation) {
      // Update existing
      setTextAnnotations((prev) =>
        prev.map((t) => (t.id === editingTextAnnotation.id ? { ...t, ...updates } : t))
      );
      toast.success('Text aktualisiert');
    } else if (textPlacementPosition) {
      // Add new
      const newAnnotation: TextAnnotation = {
        id: `text-${Date.now()}`,
        type: 'text-annotation',
        position: textPlacementPosition,
        text: updates.text || '',
        fontSize: updates.fontSize || 16,
        color: updates.color || '#000000',
        rotation: updates.rotation || 0,
        alignment: updates.alignment || 'left',
        backgroundColor: updates.backgroundColor,
        backgroundOpacity: updates.backgroundOpacity,
      };
      setTextAnnotations((prev) => [...prev, newAnnotation]);
      toast.success('Text hinzugefügt');
    }
    
    setEditingTextAnnotation(null);
    setTextPlacementPosition(null);
  }, [editingTextAnnotation, textPlacementPosition]);

  // Handle block display override save
  const handleBlockDisplayOverrideSave = useCallback((overrides: Partial<ViewDisplaySettings> | undefined, colorOverride?: string) => {
    if (blockDisplayOverrideId) {
      handleBlockUpdate(blockDisplayOverrideId, { 
        viewDisplayOverrides: overrides,
        colorOverride: colorOverride 
      });
      if (overrides) {
        toast.success('Block-Anzeigeeinstellungen überschrieben');
      } else {
        toast.success('Block-Anzeigeeinstellungen zurückgesetzt');
      }
    }
    setBlockDisplayOverrideId(null);
  }, [blockDisplayOverrideId, handleBlockUpdate]);

  // Handle block attribute operations
  const handleBlockAttributeUpdate = useCallback(
    (blockId: string, attrId: string, updates: Partial<PartAttribute>) => {
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.id === blockId) {
            return {
              ...block,
              blockData: {
                ...block.blockData,
                attributes: block.blockData.attributes.map((attr) =>
                  attr.id === attrId ? { ...attr, ...updates } : attr
                ),
              },
            };
          }
          return block;
        })
      );
    },
    []
  );

  const handleBlockAttributeDelete = useCallback(
    (blockId: string, attrId: string) => {
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.id === blockId) {
            return {
              ...block,
              blockData: {
                ...block.blockData,
                attributes: block.blockData.attributes.filter((attr) => attr.id !== attrId),
              },
            };
          }
          return block;
        })
      );
    },
    []
  );

  // Handle text annotation delete
  const handleTextAnnotationDelete = useCallback((id: string) => {
    setTextAnnotations((prev) => prev.filter((t) => t.id !== id));
    setSelectedTextIds((prev) => prev.filter((tid) => tid !== id));
    toast.success('Text gelöscht');
  }, []);

  // Handle system attribute operations
  const handleSystemAttributeAdd = useCallback(
    (systemId: string, attribute: Omit<PartAttribute, 'id'>) => {
      const newAttr: PartAttribute = {
        ...attribute,
        id: `attr-${Date.now()}`,
      };
      setSystems((prev) =>
        prev.map((s) =>
          s.id === systemId
            ? { ...s, attributes: [...(s.attributes || []), newAttr] }
            : s
        )
      );
    },
    []
  );

  const handleSystemAttributeUpdate = useCallback(
    (systemId: string, attrId: string, updates: Partial<PartAttribute>) => {
      setSystems((prev) =>
        prev.map((s) =>
          s.id === systemId
            ? {
                ...s,
                attributes: (s.attributes || []).map((attr) =>
                  attr.id === attrId ? { ...attr, ...updates } : attr
                ),
              }
            : s
        )
      );
    },
    []
  );

  const handleSystemAttributeDelete = useCallback((systemId: string, attrId: string) => {
    setSystems((prev) =>
      prev.map((s) =>
        s.id === systemId
          ? { ...s, attributes: (s.attributes || []).filter((attr) => attr.id !== attrId) }
          : s
      )
    );
  }, []);

  // Handle connection attribute operations
  const handleConnectionAttributeAdd = useCallback(
    (connectionId: string, attribute: Omit<PartAttribute, 'id'>) => {
      const newAttr: PartAttribute = {
        ...attribute,
        id: `attr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };
      setConnections((prev) =>
        prev.map((c) =>
          c.id === connectionId
            ? { ...c, attributes: [...(c.attributes || []), newAttr] }
            : c
        )
      );
      toast.success('Verbindungs-Attribut hinzugefügt');
    },
    []
  );

  const handleConnectionAttributeUpdate = useCallback(
    (connectionId: string, attrId: string, updates: Partial<PartAttribute>) => {
      setConnections((prev) =>
        prev.map((c) =>
          c.id === connectionId
            ? {
                ...c,
                attributes: (c.attributes || []).map((attr) =>
                  attr.id === attrId ? { ...attr, ...updates } : attr
                ),
              }
            : c
        )
      );
    },
    []
  );

  const handleConnectionAttributeDelete = useCallback((connectionId: string, attrId: string) => {
    setConnections((prev) =>
      prev.map((c) =>
        c.id === connectionId
          ? { ...c, attributes: (c.attributes || []).filter((attr) => attr.id !== attrId) }
          : c
      )
    );
    toast.success('Verbindungs-Attribut gelöscht');
  }, []);

  // DEPRECATED: Replaced by Ctrl+C/Ctrl+V attribute copy/paste
  // Kept for reference, can be removed in future cleanup
  // const handleConnectionAttributesCopyToSystem = useCallback((connectionId: string) => {
  //   // (Old implementation removed - use Ctrl+C/V instead)
  // }, [connections]);

  // Label template handlers
  const handleLabelTemplateAdd = useCallback((template: LabelTemplate) => {
    setLabelTemplates((prev) => [...prev, template]);
    toast.success('Beschriftung hinzugefügt');
  }, []);

  const handleLabelTemplateUpdate = useCallback((template: LabelTemplate) => {
    setLabelTemplates((prev) => prev.map((t) => (t.id === template.id ? template : t)));
    
    // Update all placed labels using this template with new global settings
    setPlacedLabels((prev) => {
      let updatedCount = 0;
      const updated = prev.map((label) => {
        if (label.templateId === template.id) {
          updatedCount++;
          
          // Re-resolve data with updated template structure
          const resolvedData = resolveLabelData(
            template,
            label.targetElementType,
            label.targetElementId,
            blocks,
            connections,
            textAnnotations,
            systems
          );
          
          return {
            ...label,
            // Update global template properties
            leaderLineWidth: template.leaderLineWidth || 1,
            // Update resolved data (in case template structure changed)
            resolvedData,
          };
        }
        return label;
      });
      
      if (updatedCount > 0) {
        console.log(`✅ Template "${template.name}" aktualisiert: ${updatedCount} platzierte Labels synchronisiert`);
        toast.success(`Template aktualisiert • ${updatedCount} Labels angepasst`);
      } else {
        toast.success('Template aktualisiert');
      }
      
      return updated;
    });
  }, [blocks, connections, textAnnotations, systems]);

  const handleLabelTemplateDelete = useCallback((templateId: string) => {
    setLabelTemplates((prev) => prev.filter((t) => t.id !== templateId));
    // Remove all placed labels using this template
    setPlacedLabels((prev) => prev.filter((l) => l.templateId !== templateId));
    toast.success('Beschriftung gelöscht');
  }, []);

  const handleLabelTemplateSelect = useCallback((template: LabelTemplate) => {
    setSelectedLabelTemplateId(template.id);
    setActiveTool('label');
    toast.info(`Beschriftung "${template.name}" ausgewählt. Klicken Sie auf ein Element zum Beschriften.`);
  }, []);

  const handleLabelAdd = useCallback((label: PlacedLabel) => {
    setPlacedLabels((prev) => [...prev, label]);
    toast.success('Beschriftung platziert');
  }, []);

  const handleLabelUpdate = useCallback((id: string, updates: Partial<PlacedLabel>) => {
    setPlacedLabels((prev) => 
      prev.map((l) => {
        if (l.id !== id) return l;
        
        const updatedLabel = { ...l, ...updates };
        
        // If target just changed from temp to real, resolve data immediately
        if (updates.targetElementId && !updates.targetElementId.startsWith('temp-')) {
          const template = labelTemplates.find((t) => t.id === updatedLabel.templateId);
          if (template) {
            console.log('🔄 Resolving data immediately for new label');
            const resolvedData = resolveLabelData(
              template,
              updatedLabel.targetElementType,
              updatedLabel.targetElementId,
              blocks,
              connections,
              textAnnotations,
              systems
            );
            updatedLabel.resolvedData = resolvedData;
            console.log('✅ Immediate resolve result:', resolvedData);
          }
        }
        
        return updatedLabel;
      })
    );
    console.log('🏷️ Label updated:', id, updates);
  }, [labelTemplates, blocks, connections, textAnnotations, systems]);

  const handleLabelEdit = useCallback((labelId: string) => {
    const label = placedLabels.find((l) => l.id === labelId);
    if (label) {
      setEditingLabel(label);
      setShowLabelEditDialog(true);
    }
  }, [placedLabels]);

  // NEW: Handle label reassignment to a new target element
  const handleLabelReassign = useCallback((labelId: string) => {
    const label = placedLabels.find((l) => l.id === labelId);
    if (!label) {
      toast.error('Label nicht gefunden');
      return;
    }

    // Enter reassignment mode
    setReassigningLabelId(labelId);
    setActiveTool('select'); // Switch to select tool
    toast.info('Klicke auf ein Element (Block oder Verbindung) um das Label neu zuzuweisen');
    console.log('🔄 Label reassignment mode activated for:', labelId);
  }, [placedLabels]);

  // Manually update active drawing in rootFolder when state changes (debounced)
  useEffect(() => {
    if (!activeDrawingId) return;
    
    const timer = setTimeout(() => {
      setRootFolder((prev) => {
        const updateDrawing = (folder: DrawingFolder): DrawingFolder => {
          return {
            ...folder,
            children: folder.children.map((child) => {
              if ('blocks' in child && child.id === activeDrawingId) {
                return {
                  ...child,
                  blocks,
                  connections,
                  systems,
                  textAnnotations,
                  labelTemplates,
                  placedLabels,
                  backgroundSettings,
                  viewDisplaySettings,
                  viewState,
                  pipeTypes,
                  plans,
                  modified: new Date().toISOString(),
                };
              } else if ('children' in child) {
                return updateDrawing(child);
              }
              return child;
            }),
          };
        };
        return updateDrawing(prev);
      });
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(timer);
  }, [activeDrawingId, blocks, connections, systems, textAnnotations, labelTemplates, placedLabels, backgroundSettings, viewDisplaySettings, viewState, pipeTypes, plans]);

  // Drawing Management Handlers
  const handleDrawingCreate = useCallback((parentFolderId: string, name: string) => {
    const newDrawing: Drawing = {
      id: `drawing-${Date.now()}`,
      name: name,
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      blocks: [],
      connections: [],
      systems: [],
      textAnnotations: [],
      labelTemplates: labelTemplates,
      placedLabels: [],
      backgroundSettings: backgroundSettings,
      viewDisplaySettings: viewDisplaySettings,
      viewState: { zoom: 1, panX: 400, panY: 300 },
      pipeTypes: pipeTypes,
    };

    setRootFolder((prev) => {
      const addToFolder = (folder: DrawingFolder): DrawingFolder => {
        if (folder.id === parentFolderId) {
          return {
            ...folder,
            children: [...folder.children, newDrawing],
          };
        }
        return {
          ...folder,
          children: folder.children.map((child) =>
            'children' in child ? addToFolder(child) : child
          ),
        };
      };
      return addToFolder(prev);
    });

    // Load the new empty drawing to canvas
    setActiveDrawingId(newDrawing.id);
    setBlocks([]);
    setConnections([]);
    setSystems([]);
    setTextAnnotations([]);
    setPlacedLabels([]);
    setPlans([]);
    setActivePlanId(undefined);
    setViewState({ zoom: 1, panX: 400, panY: 300 });
    toast.success(`Zeichnung "${name}" erstellt`);
  }, [labelTemplates, backgroundSettings, viewDisplaySettings, pipeTypes]);

  const handleDrawingSelect = useCallback((drawingId: string) => {
    console.log('📁 DEBUG: Drawing selected:', drawingId);
    setActiveDrawingId(drawingId);
    const findDrawing = (folder: DrawingFolder): Drawing | null => {
      for (const child of folder.children) {
        if ('blocks' in child && child.id === drawingId) {
          return child;
        } else if ('children' in child) {
          const found = findDrawing(child);
          if (found) return found;
        }
      }
      return null;
    };

    const drawing = findDrawing(rootFolder);
    if (drawing) {
      console.log('📁 DEBUG: Loading drawing data:', {
        blocksCount: drawing.blocks?.length || 0,
        connectionsCount: drawing.connections?.length || 0,
        systemsCount: drawing.systems?.length || 0,
        placedLabelsCount: drawing.placedLabels?.length || 0,
        connectionsWithAttributes: drawing.connections?.filter(c => c.attributes && c.attributes.length > 0).length || 0
      });
      
      // Update length attributes when loading drawing
      const gridSize = drawing.backgroundSettings?.isometricGridSize || 200;
      const blocksWithLengths = updateAllLengthAttributes(drawing.blocks, gridSize);
      setBlocks(blocksWithLengths);
      setConnections(drawing.connections);
      setSystems(drawing.systems);
      setTextAnnotations(drawing.textAnnotations || []);
      setLabelTemplates(drawing.labelTemplates || []);
      setPlacedLabels(drawing.placedLabels || []);
      setBackgroundSettings(drawing.backgroundSettings || backgroundSettings);
      setViewDisplaySettings(drawing.viewDisplaySettings || viewDisplaySettings);
      setViewState(drawing.viewState || { zoom: 1, panX: 400, panY: 300 });
      setPipeTypes(drawing.pipeTypes || pipeTypes);
      setPlans(drawing.plans || []);
      setActivePlanId(undefined);
      toast.success(`Zeichnung "${drawing.name}" geladen`);
    }
  }, [rootFolder, backgroundSettings, viewDisplaySettings, pipeTypes]);

  const handleFolderToggle = useCallback((folderId: string) => {
    setRootFolder((prev) => {
      const toggleFolder = (folder: DrawingFolder): DrawingFolder => {
        if (folder.id === folderId) {
          return { ...folder, expanded: !folder.expanded };
        }
        return {
          ...folder,
          children: folder.children.map((child) =>
            'children' in child ? toggleFolder(child) : child
          ),
        };
      };
      return toggleFolder(prev);
    });
  }, []);

  const handleDrawingRename = useCallback((drawingId: string, newName: string) => {
    setRootFolder((prev) => {
      const renameDrawing = (folder: DrawingFolder): DrawingFolder => {
        return {
          ...folder,
          children: folder.children.map((child) => {
            if ('blocks' in child && child.id === drawingId) {
              return { ...child, name: newName, modified: new Date().toISOString() };
            } else if ('children' in child) {
              return renameDrawing(child);
            }
            return child;
          }),
        };
      };
      return renameDrawing(prev);
    });
    toast.success(`Zeichnung umbenannt zu "${newName}"`);
  }, []);

  const handleFolderCreate = useCallback((parentFolderId: string, name: string) => {
    const newFolder: DrawingFolder = {
      id: `folder-${Date.now()}`,
      name: name,
      children: [],
      expanded: true,
    };

    setRootFolder((prev) => {
      const addFolder = (folder: DrawingFolder): DrawingFolder => {
        if (folder.id === parentFolderId) {
          return {
            ...folder,
            children: [...folder.children, newFolder],
          };
        }
        return {
          ...folder,
          children: folder.children.map((child) =>
            'children' in child ? addFolder(child) : child
          ),
        };
      };
      return addFolder(prev);
    });
    toast.success(`Ordner "${name}" erstellt`);
  }, []);

  const handleFolderRename = useCallback((folderId: string, newName: string) => {
    setRootFolder((prev) => {
      const renameFolder = (folder: DrawingFolder): DrawingFolder => {
        if (folder.id === folderId) {
          return { ...folder, name: newName };
        }
        return {
          ...folder,
          children: folder.children.map((child) =>
            'children' in child ? renameFolder(child) : child
          ),
        };
      };
      return renameFolder(prev);
    });
    toast.success(`Ordner umbenannt zu "${newName}"`);
  }, []);

  const handleFolderDelete = useCallback((folderId: string) => {
    setRootFolder((prev) => {
      const deleteFolder = (folder: DrawingFolder): DrawingFolder => {
        return {
          ...folder,
          children: folder.children.filter((child) => {
            if ('children' in child && child.id === folderId) {
              return false;
            }
            return true;
          }).map((child) =>
            'children' in child ? deleteFolder(child) : child
          ),
        };
      };
      return deleteFolder(prev);
    });
    toast.success('Ordner gelöscht');
  }, []);

  const handleDrawingDelete = useCallback((drawingId: string) => {
    setRootFolder((prev) => {
      const deleteDrawing = (folder: DrawingFolder): DrawingFolder => {
        return {
          ...folder,
          children: folder.children.filter((child) => {
            if ('blocks' in child && child.id === drawingId) {
              return false;
            }
            return true;
          }).map((child) =>
            'children' in child ? deleteDrawing(child) : child
          ),
        };
      };
      return deleteDrawing(prev);
    });
    
    if (activeDrawingId === drawingId) {
      setActiveDrawingId(undefined);
    }
    toast.success('Zeichnung gelöscht');
  }, [activeDrawingId]);

  const handleDrawingDuplicate = useCallback((drawingId: string) => {
    setRootFolder((prev) => {
      const findAndDuplicate = (folder: DrawingFolder): { folder: DrawingFolder; duplicated: boolean } => {
        for (let i = 0; i < folder.children.length; i++) {
          const child = folder.children[i];
          if ('blocks' in child && child.id === drawingId) {
            const duplicate: Drawing = {
              ...child,
              id: `drawing-${Date.now()}`,
              name: `${child.name} (Kopie)`,
              created: new Date().toISOString(),
              modified: new Date().toISOString(),
            };
            return {
              folder: {
                ...folder,
                children: [...folder.children.slice(0, i + 1), duplicate, ...folder.children.slice(i + 1)],
              },
              duplicated: true,
            };
          } else if ('children' in child) {
            const result = findAndDuplicate(child);
            if (result.duplicated) {
              return {
                folder: {
                  ...folder,
                  children: folder.children.map((c) => c.id === child.id ? result.folder : c),
                },
                duplicated: true,
              };
            }
          }
        }
        return { folder, duplicated: false };
      };
      return findAndDuplicate(prev).folder;
    });
    toast.success('Zeichnung dupliziert');
  }, []);

  const handleItemMove = useCallback((itemId: string, targetFolderId: string) => {
    setRootFolder((prev) => {
      let movedItem: Drawing | DrawingFolder | null = null;
      
      // Remove item from its current location
      const removeItem = (folder: DrawingFolder): DrawingFolder => {
        const filtered = folder.children.filter((child) => {
          if (child.id === itemId) {
            movedItem = child;
            return false;
          }
          return true;
        });
        
        return {
          ...folder,
          children: filtered.map((child) =>
            'children' in child ? removeItem(child) : child
          ),
        };
      };
      
      const withoutItem = removeItem(prev);
      
      if (!movedItem) {
        console.error('Item not found:', itemId);
        return prev;
      }
      
      // Add item to target folder
      const addItem = (folder: DrawingFolder): DrawingFolder => {
        if (folder.id === targetFolderId) {
          return {
            ...folder,
            children: [...folder.children, movedItem!],
          };
        }
        return {
          ...folder,
          children: folder.children.map((child) =>
            'children' in child ? addItem(child) : child
          ),
        };
      };
      
      return addItem(withoutItem);
    });
    toast.success('Element verschoben');
  }, []);

  const handleLabelEditSave = useCallback((updates: Partial<PlacedLabel>) => {
    if (editingLabel) {
      handleLabelUpdate(editingLabel.id, updates);
      toast.success('Beschriftung aktualisiert');
    }
  }, [editingLabel, handleLabelUpdate]);

  const handleLabelDelete = useCallback((id: string) => {
    setPlacedLabels((prev) => prev.filter((l) => l.id !== id));
    setSelectedLabelIds((prev) => prev.filter((lid) => lid !== id));
    toast.success('Beschriftung gelöscht');
  }, []);

  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const hasRunLabelCleanup = React.useRef(false);

  // Auto-update labels when blocks, connections, or textAnnotations change
  useEffect(() => {
    console.log('🏷️ DEBUG: Label auto-update triggered. Dependencies changed:', {
      blocksCount: blocks.length,
      connectionsCount: connections.length,
      textAnnotationsCount: textAnnotations.length,
      systemsCount: systems.length,
      labelTemplatesCount: labelTemplates.length
    });
    
    // Update all labels to re-resolve their data
    setPlacedLabels((currentLabels) => {
      console.log('🏷️ DEBUG: Current placedLabels count:', currentLabels.length);
      let hasChanges = false;
      let removedCount = 0;
      
      // ⚠️ LABELS ARE 100% PERSISTENT - DO NOT FILTER OR REMOVE!
      // Keep ALL labels, even if their targets don't exist anymore
      // User will manually delete invalid labels if needed
      
      // Update ALL labels (no filtering)
      const updatedLabels = currentLabels.map((label) => {
        const template = labelTemplates.find((t) => t.id === label.templateId);
        if (!template) {
          console.warn('⚠️ Template not found for label:', label.id, 'templateId:', label.templateId);
          return label;
        }

        // Skip labels with temporary IDs (they are being placed)
        if (label.targetElementId.startsWith('temp-')) {
          return label;
        }

        // Re-resolve the label data with current element attributes
        const resolvedData = resolveLabelData(
          template,
          label.targetElementType,
          label.targetElementId,
          blocks,
          connections,
          textAnnotations,
          systems
        );

        // Check if data actually changed (deep comparison of resolved data)
        const hasDataChanged = JSON.stringify(label.resolvedData) !== JSON.stringify(resolvedData);
        if (hasDataChanged) {
          hasChanges = true;
          console.log('🔄 Label auto-updated:', label.id, 'for', label.targetElementType, label.targetElementId);
        }

        return {
          ...label,
          resolvedData,
        };
      });

      // Only return updated labels if there were actual changes
      if (hasChanges) {
        console.log(`✅ ${updatedLabels.filter((l, i) => JSON.stringify(l.resolvedData) !== JSON.stringify(currentLabels[i]?.resolvedData)).length} label(s) aktualisiert`);
      }
      return hasChanges ? updatedLabels : currentLabels;
    });
  }, [blocks, connections, textAnnotations, systems, labelTemplates]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.min(10, prev.zoom * 1.2),
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(0.01, prev.zoom / 1.2),
    }));
  }, []);

  const handleZoomReset = useCallback(() => {
    setViewState({
      zoom: 1,
      panX: 400,
      panY: 300,
    });
  }, []);

  // Fit all blocks to view
  const handleFitToView = useCallback(() => {
    if (blocks.length === 0) {
      toast.info('Keine Blöcke zum Anzeigen');
      return;
    }

    // Find bounding box of all blocks
    let minU = Infinity, maxU = -Infinity;
    let minV = Infinity, maxV = -Infinity;

    blocks.forEach(block => {
      block.blockData.elements.forEach(el => {
        if (el.type === 'line') {
          const start = { u: el.start.u + block.transform.position.u, v: el.start.v + block.transform.position.v };
          const end = { u: el.end.u + block.transform.position.u, v: el.end.v + block.transform.position.v };
          minU = Math.min(minU, start.u, end.u);
          maxU = Math.max(maxU, start.u, end.u);
          minV = Math.min(minV, start.v, end.v);
          maxV = Math.max(maxV, start.v, end.v);
        } else if (el.type === 'connection') {
          const pos = { u: el.position.u + block.transform.position.u, v: el.position.v + block.transform.position.v };
          minU = Math.min(minU, pos.u);
          maxU = Math.max(maxU, pos.u);
          minV = Math.min(minV, pos.v);
          maxV = Math.max(maxV, pos.v);
        }
      });
    });

    // Check for invalid coordinates
    if (!isFinite(minU) || !isFinite(maxU) || !isFinite(minV) || !isFinite(maxV)) {
      toast.error('Blöcke haben ungültige Koordinaten');
      return;
    }

    const canvas = document.querySelector('canvas');
    if (!canvas) return;

    const width = canvas.width;
    const height = canvas.height;
    const gridSize = backgroundSettings.isometricGridSize;

    // Convert corners to screen coordinates (at zoom=1, pan=0) to find bounding box in screen space
    const corners = [
      { u: minU, v: minV },
      { u: maxU, v: minV },
      { u: maxU, v: maxV },
      { u: minU, v: maxV },
    ];

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    corners.forEach(p => {
      const screenP = isoToScreen(p, gridSize);
      minX = Math.min(minX, screenP.x);
      maxX = Math.max(maxX, screenP.x);
      minY = Math.min(minY, screenP.y);
      maxY = Math.max(maxY, screenP.y);
    });

    // Calculate content dimensions and center
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    // Calculate optimal zoom
    const padding = 100; // pixels
    const availableWidth = Math.max(100, width - padding * 2);
    const availableHeight = Math.max(100, height - padding * 2);

    const zoomX = availableWidth / Math.max(1, contentWidth);
    const zoomY = availableHeight / Math.max(1, contentHeight);
    
    // Limit zoom to reasonable values
    const zoom = Math.max(0.05, Math.min(2.0, Math.min(zoomX, zoomY)));

    // Calculate pan to center the content
    // ScreenCenter = WorldCenter * Zoom + Pan
    // Pan = ScreenCenter - WorldCenter * Zoom
    const panX = (width / 2) - (centerX * zoom);
    const panY = (height / 2) - (centerY * zoom);

    // Set view to center
    setViewState({
      zoom,
      panX,
      panY,
    });

    toast.success('Ansicht zentriert');
  }, [blocks, backgroundSettings.isometricGridSize]);

  // Clear all blocks
  const handleClearAll = useCallback(() => {
    if (blocks.length === 0 && textAnnotations.length === 0) return;
    
    if (window.confirm(`Wirklich ALLES löschen? ${blocks.length} Blöcke, ${connections.length} Verbindungen, ${textAnnotations.length} Texte`)) {
      console.log('🗑️ NUCLEAR DELETE: Clearing everything...');
      
      // Clear ALL state
      setBlocks([]);
      setConnections([]);
      setSystems([]);
      setTextAnnotations([]);
      setPlacedLabels([]);
      setSelectedBlockIds([]);
      setSelectedTextIds([]);
      setConnectionMarkers([]);
      
      // CRITICAL: Also clear localStorage immediately!
      localStorage.removeItem('blocksystem-blocks');
      localStorage.removeItem('blocksystem-connections');
      localStorage.removeItem('blocksystem-systems');
      localStorage.removeItem('blocksystem-textannotations');
      localStorage.removeItem('blocksystem-placedlabels');
      localStorage.removeItem('blocksystem-connectionmarkers');
      
      console.log('✅ NUCLEAR DELETE: Everything cleared!');
      toast.success('Alles gelöscht (inkl. localStorage)');
    }
  }, [blocks.length, connections.length, textAnnotations.length]);

  const handleDeletePlan = useCallback((id: string) => {
    if (!window.confirm('Plan wirklich löschen?')) return;
    setPlans(prev => prev.filter(p => p.id !== id));
    if (activePlanId === id) setActivePlanId(undefined);
    toast.success('Plan gelöscht');
  }, [activePlanId]);

  // NEW: Handle selection of a plan from the browser
  const handlePlanSelect = useCallback((planId: string, drawingId: string) => {
    if (drawingId !== activeDrawingId) {
       // Load the drawing first
       handleDrawingSelect(drawingId);
       // Wait for state update - simple workaround
       setTimeout(() => setActivePlanId(planId), 100);
    } else {
       setActivePlanId(planId);
    }
  }, [activeDrawingId, handleDrawingSelect]);
  
  const handlePlanDeleteFromBrowser = useCallback((planId: string, drawingId: string) => {
    if (drawingId === activeDrawingId) {
      handleDeletePlan(planId);
    } else {
      // Delete from non-active drawing in rootFolder
      setRootFolder(prev => {
         const deleteFromFolder = (folder: DrawingFolder): DrawingFolder => {
           return {
             ...folder,
             children: folder.children.map(child => {
               if ('blocks' in child) { // is Drawing
                 if (child.id === drawingId) {
                   return {
                     ...child,
                     plans: (child.plans || []).filter(p => p.id !== planId)
                   };
                 }
                 return child;
               }
               return deleteFromFolder(child); // is Folder
             })
           }
         };
         return deleteFromFolder(prev);
      });
      toast.success('Plan gelöscht');
    }
  }, [activeDrawingId, handleDeletePlan]);

  const handleCreatePlan = useCallback((drawingId?: string) => {
    // If drawingId is provided and different from active, switch first
    if (drawingId && drawingId !== activeDrawingId) {
      handleDrawingSelect(drawingId);
      // We rely on the user to click again or switch context, but for "Create" button
      // on a non-active drawing, we probably want to switch context AND create.
      // But creating relies on "plans" state which is tied to active drawing.
      // So we must wait for switch.
      setTimeout(() => {
          // Recursive call after switch? Or duplicating logic?
          // Duplicating logic is safer for async state
          setPlans(prev => {
              const newPlan: PlanSheet = {
                  id: `plan-${Date.now()}`,
                  name: `Plan ${prev.length + 1}`,
                  created: new Date().toISOString(),
                  modified: new Date().toISOString(),
                  format: 'A3',
                  orientation: 'landscape',
                  width: 420,
                  height: 297,
                  viewport: { centerX: 0, centerY: 0, scale: 0.01 },
                  titleBlockTemplateId: 'default',
                  titleBlockData: {
                    projectName: projectName,
                    client: '',
                    drawnBy: 'User',
                    date: new Date().toLocaleDateString()
                  },
                  revisions: [],
                  showCropMarks: true,
                  showLegend: false
                };
                return [...prev, newPlan];
          });
          // We can't set activePlanId easily here because we don't know the ID yet outside setPlans
          // So let's stick to simple "Create in active drawing"
          toast.success('Neuer Plan in ausgewählter Zeichnung erstellt');
      }, 100);
      return; 
    }

    const newPlan: PlanSheet = {
      id: `plan-${Date.now()}`,
      name: `Plan ${plans.length + 1}`,
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      format: 'A3',
      orientation: 'landscape',
      width: 420,
      height: 297,
      viewport: {
        centerX: 0,
        centerY: 0,
        scale: 0.01
      },
      titleBlockTemplateId: 'default',
      titleBlockData: {
        projectName: projectName,
        client: '',
        drawnBy: 'User',
        date: new Date().toLocaleDateString()
      },
      revisions: [],
      showCropMarks: true,
      showLegend: false
    };
    
    setPlans(prev => [...prev, newPlan]);
    setActivePlanId(newPlan.id);
    toast.success('Neuer Plan erstellt');
  }, [plans.length, projectName, activeDrawingId, handleDrawingSelect]);

  const handleUpdatePlan = useCallback((id: string, updates: Partial<PlanSheet>) => {
    setPlans(prev => prev.map(p => {
      if (p.id !== id) return p;
      
      const updated = { ...p, ...updates, modified: new Date().toISOString() };
      
      // Auto-update dimensions if format changes
      if (updates.format || updates.orientation) {
        const fmt = updates.format || p.format;
        const orient = updates.orientation || p.orientation;
        
        let w = 420, h = 297; // A3 default
        if (fmt === 'A4') { w = 210; h = 297; }
        else if (fmt === 'A3') { w = 297; h = 420; }
        else if (fmt === 'A2') { w = 420; h = 594; }
        else if (fmt === 'A1') { w = 594; h = 841; }
        else if (fmt === 'A0') { w = 841; h = 1189; }
        
        if (orient === 'landscape') {
          const temp = w; w = h; h = temp;
        }
        
        if (fmt !== 'Custom') {
           updated.width = w;
           updated.height = h;
        }
      }
      
      return updated;
    }));
  }, []);

  const handleDuplicatePlan = useCallback((id: string) => {
     const plan = plans.find(p => p.id === id);
     if (!plan) return;
     
     const newPlan = {
       ...plan,
       id: `plan-${Date.now()}`,
       name: `${plan.name} (Kopie)`,
       created: new Date().toISOString(),
       modified: new Date().toISOString()
     };
     
     setPlans(prev => [...prev, newPlan]);
     toast.success('Plan dupliziert');
  }, [plans]);

  // Helper to generate project data
  const getProjectData = useCallback((filename: string) => {
    const now = new Date().toISOString();

    // Load current print settings from localStorage
    const currentPrintConfig = loadPrintConfig();
    const allPresets = loadAllPresets();
    const customPresets = allPresets.filter(p => !p.isBuiltIn);

    // 🔥 CRITICAL: Save current state back to active drawing BEFORE exporting!
    let folderToSave = rootFolder;
    if (rootFolder && activeDrawingId) {
      console.log('💾 Saving current state to active drawing before export...');
      
      const updateDrawing = (folder: any): any => {
        return {
          ...folder,
          children: folder.children.map((child: any) => {
            if ('blocks' in child && child.id === activeDrawingId) {
              // This is the active drawing - update it with current state!
              console.log(`✅ Updated drawing "${child.name}" with current state`);
              return {
                ...child,
                modified: now,
                blocks,
                connections,
                systems,
                textAnnotations,
                labelTemplates,
                placedLabels,
                backgroundSettings,
                viewDisplaySettings,
                viewState,
                pipeTypes,
                // Save plans associated with this drawing
                plans: plans,
                titleBlockTemplates: titleBlockTemplates,
              };
            } else if ('children' in child) {
              // It's a folder - recurse
              return updateDrawing(child);
            }
            return child;
          }),
        };
      };
      
      folderToSave = updateDrawing(rootFolder);
    }

    const project: IsomProject = {
      name: filename,
      version: '2.0',
      created: now,
      modified: now,
      blocks,
      connections,
      systems,
      plans,
      textAnnotations,
      labelTemplates,
      placedLabels,
      backgroundSettings,
      viewDisplaySettings,
      viewState,
      pipeTypes,
      snapEnabled,
      rootFolder: folderToSave,
      activeDrawingId,
      lastPrintConfig: currentPrintConfig || undefined,
      printPresets: customPresets.length > 0 ? customPresets : undefined,
      titleBlockTemplates,
    };

    const blob = new Blob([JSON.stringify(project, null, 2)], {
      type: 'application/json',
    });

    return blob;
  }, [blocks, connections, systems, plans, textAnnotations, labelTemplates, placedLabels, backgroundSettings, viewDisplaySettings, viewState, pipeTypes, snapEnabled, rootFolder, activeDrawingId, titleBlockTemplates]);

  // Save As - always ask for new filename
  const handleSaveAs = useCallback(async () => {
    // Check for File System Access API
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: `${projectName}.isomproject`,
          types: [{
            description: 'IsoCAD Projekt',
            accept: { 'application/json': ['.isomproject'] }
          }],
        });

        const name = handle.name.replace(/\.isomproject$/i, '');
        setProjectName(name);
        
        const blob = getProjectData(name);
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        toast.success(t('editor.status.saved'));
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Save failed:', err);
          toast.error('Fehler beim Speichern');
        } else {
          toast.info('Speichern abgebrochen');
        }
      }
    } else {
      // Fallback: Trigger download directly to allow browser's "Save As" dialog (if configured)
      // This allows selecting a folder, which prompt() prevents.
      const filename = projectName;
      const blob = getProjectData(filename);
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename.replace(/\s+/g, '_')}.isomproject`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Projekt exportiert (Browser-Download)`);
    }
  }, [projectName, getProjectData]);

  // Save project
  const handleSave = useCallback(async () => {
    if (projectName === 'Neues Projekt') {
      await handleSaveAs();
      return;
    }
    
    const blob = getProjectData(projectName);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName.replace(/\s+/g, '_')}.isomproject`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success(t('editor.status.saved'));
  }, [projectName, handleSaveAs, getProjectData]);

    // Load project
    const handleLoad = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const project: IsomProject = JSON.parse(event.target?.result as string);

        // Migrate blocks to ensure compatibility with old formats
        const migratedBlocks = migrateBlockData(project.blocks || []);
        
        // Update length attributes for all line components
        const gridSize = project.backgroundSettings?.isometricGridSize || 200;
        const blocksWithLengths = updateAllLengthAttributes(migratedBlocks, gridSize);
        
        // Re-detect systems from loaded data
        const loadedConnections = project.connections || [];
        const loadedSystems = project.systems || [];
        
        const systemResult = detectSystems(blocksWithLengths, loadedConnections, loadedSystems);
        
        setBlocks(systemResult.blocks);
        setConnections(loadedConnections);
        setSystems(systemResult.systems);
        setPlans(project.plans || []);
        setTitleBlockTemplates(project.titleBlockTemplates || []); // Load title block templates
        setTextAnnotations(project.textAnnotations || []); // Load text annotations
        setLabelTemplates(project.labelTemplates || []); // Load label templates
        
        // ⚠️ LABELS ARE 100% PERSISTENT - Load ALL labels without validation
        // Even if targets don't exist, labels remain visible
        // User can manually delete invalid labels if needed
        const loadedLabels = project.placedLabels || [];
        console.log(`ℹ️ Loading ${loadedLabels.length} labels (no validation - persistent mode)`);
        
        setPlacedLabels(loadedLabels);
        setProjectName(project.name);

        if (project.backgroundSettings) {
          setBackgroundSettings(project.backgroundSettings);
        }

        // Restore view display settings if available
        if (project.viewDisplaySettings) {
          setViewDisplaySettings({
            lineWidthMultiplier: project.viewDisplaySettings.lineWidthMultiplier ?? 1.0,
            connectionSizeMultiplier: project.viewDisplaySettings.connectionSizeMultiplier ?? 1.0,
            fontSizeMultiplier: project.viewDisplaySettings.fontSizeMultiplier ?? 1.0,
            iconSizeMultiplier: project.viewDisplaySettings.iconSizeMultiplier ?? 1.0,
          });
        }

        // Restore view state (camera position and zoom) if available
        if (project.viewState) {
          setViewState(project.viewState);
        }

        // Restore pipe types if available
        if (project.pipeTypes && project.pipeTypes.length > 0) {
          setPipeTypes(project.pipeTypes);
          // Set first pipe type as selected
          setSelectedPipeTypeId(project.pipeTypes[0].id);
        }

        // Restore snap-to-grid setting if available
        if (typeof project.snapEnabled !== 'undefined') {
          setSnapEnabled(project.snapEnabled);
        }

        // Restore print configuration and presets
        if (project.lastPrintConfig) {
          try {
            savePrintConfig(project.lastPrintConfig);
            console.log('✅ Restored print configuration from project');
          } catch (error) {
            console.error('⚠️ Failed to restore print config:', error);
          }
        }
        
        if (project.printPresets && project.printPresets.length > 0) {
          try {
            // Convert date strings back to Date objects
            const restoredPresets = project.printPresets.map(preset => ({
              ...preset,
              createdAt: new Date(preset.createdAt),
              updatedAt: preset.updatedAt ? new Date(preset.updatedAt) : undefined,
            }));
            saveCustomPresets(restoredPresets);
            console.log(`✅ Restored ${restoredPresets.length} custom print preset(s)`);
          } catch (error) {
            console.error('⚠️ Failed to restore print presets:', error);
          }
        }

        // NEW: Load complete browser structure if available
        if (project.rootFolder) {
          console.log('📂 Loading complete project structure with folders and drawings');
          setRootFolder(project.rootFolder);
          
          // Load the active drawing if specified
          if (project.activeDrawingId) {
            setActiveDrawingId(project.activeDrawingId);
            
            // Find and load the active drawing's content
            const findDrawing = (folder: any, id: string): any => {
              for (const child of folder.children) {
                if ('blocks' in child && child.id === id) return child;
                if ('children' in child) {
                  const found = findDrawing(child, id);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const activeDrawing = findDrawing(project.rootFolder, project.activeDrawingId);
            if (activeDrawing) {
              console.log('📄 Loading active drawing:', activeDrawing.name);
              const migratedDrawingBlocks = migrateBlockData(activeDrawing.blocks || []);
              setBlocks(migratedDrawingBlocks);
              setConnections(activeDrawing.connections || []);
              setSystems(activeDrawing.systems || []);
              setTextAnnotations(activeDrawing.textAnnotations || []);
              setLabelTemplates(activeDrawing.labelTemplates || []);
              setPlacedLabels(activeDrawing.placedLabels || []);
              if (activeDrawing.backgroundSettings) setBackgroundSettings(activeDrawing.backgroundSettings);
              if (activeDrawing.viewDisplaySettings) setViewDisplaySettings(activeDrawing.viewDisplaySettings);
              if (activeDrawing.viewState) setViewState(activeDrawing.viewState);
              if (activeDrawing.pipeTypes) setPipeTypes(activeDrawing.pipeTypes);
              // NEW: Load plans from the active drawing
              setPlans(activeDrawing.plans || []);
              setActivePlanId(undefined);
            }
          }
        } else {
          // Legacy format - single drawing, create a simple structure
          console.log('📄 Loading legacy project format (single drawing)');
          const drawing: any = {
            id: `drawing-${Date.now()}`,
            name: project.name,
            created: project.created,
            modified: project.modified,
            blocks: migratedBlocks,
            connections: project.connections || [],
            systems: project.systems || [],
            textAnnotations: project.textAnnotations || [],
            labelTemplates: project.labelTemplates || [],
            placedLabels: project.placedLabels || [],
            backgroundSettings: project.backgroundSettings,
            viewDisplaySettings: project.viewDisplaySettings,
            viewState: project.viewState,
            pipeTypes: project.pipeTypes,
            plans: project.plans || [], // NEW: Associate loaded plans with this drawing
          };
          
          setRootFolder({
            id: 'root',
            name: 'Root',
            children: [drawing],
            expanded: true,
          });
          setActiveDrawingId(drawing.id);
        }

        // Add unique parts from project to library
        if (migratedBlocks.length > 0) {
          try {
            // Get current library from localStorage
            const libraryJson = localStorage.getItem('isompart-library');
            const currentLibrary: IsomPart[] = libraryJson ? JSON.parse(libraryJson) : [];
            
            // Extract unique IsomPart objects from blocks
            const uniqueParts = new Map<string, IsomPart>();
            
            migratedBlocks.forEach(block => {
              const part = block.blockData;
              // Use name + created timestamp as unique key (if created exists), otherwise just name
              const key = part.created ? `${part.name}-${part.created}` : part.name;
              
              // Only add if not already in map (first occurrence wins)
              if (!uniqueParts.has(key)) {
                uniqueParts.set(key, part);
              }
            });
            
            // Check which parts are new (not in current library)
            const newParts: IsomPart[] = [];
            uniqueParts.forEach(part => {
              const existsInLibrary = currentLibrary.some(libPart => 
                libPart.name === part.name && libPart.created === part.created
              );
              
              if (!existsInLibrary) {
                newParts.push(part);
              }
            });
            
            // Add new parts to library
            if (newParts.length > 0) {
              const updatedLibrary = [...currentLibrary, ...newParts];
              localStorage.setItem('isompart-library', JSON.stringify(updatedLibrary));
              console.log(`📚 Added ${newParts.length} new part(s) to library from project`);
              
              // Trigger library update event
              window.dispatchEvent(new CustomEvent('isompart-library-updated'));
              
              toast.success(`${newParts.length} neue Bauteile zur Bibliothek hinzugefügt`);
            }
          } catch (error) {
            console.error('❌ Error adding parts to library:', error);
          }
        }

        // Build success message with details
        const details = [];
        if (project.textAnnotations && project.textAnnotations.length > 0) {
          details.push(`${project.textAnnotations.length} Text${project.textAnnotations.length === 1 ? '' : 'e'}`);
        }
        if (project.labelTemplates && project.labelTemplates.length > 0) {
          details.push(`${project.labelTemplates.length} Template${project.labelTemplates.length === 1 ? '' : 's'}`);
        }
        if (project.placedLabels && project.placedLabels.length > 0) {
          details.push(`${project.placedLabels.length} Label${project.placedLabels.length === 1 ? '' : 's'}`);
        }
        if (project.viewDisplaySettings) details.push('Ansichtseinstellungen');
        if (project.viewState) details.push('Kameraposition');
        if (project.pipeTypes && project.pipeTypes.length > 0) details.push('Leitungstypen');
        if (project.lastPrintConfig) details.push('Druckkonfiguration');
        if (project.printPresets && project.printPresets.length > 0) details.push(`${project.printPresets.length} Druck-Preset${project.printPresets.length === 1 ? '' : 's'}`);
        
        const detailsStr = details.length > 0 ? ` (inkl. ${details.join(', ')})` : '';
        toast.success(`Projekt "${project.name}" geladen${detailsStr}`);
      } catch (error) {
        toast.error('Fehler beim Laden der Projektdatei');
        console.error(error);
      }
    };
    reader.readAsText(file);

    // Reset input so the same file can be loaded again
    e.target.value = '';
  }, []); // No dependencies needed - uses imported ensureSystemsCorrect

  // New project
  const handleNewProject = useCallback(() => {
    if (blocks.length > 0 && !window.confirm('Aktuelles Projekt verwerfen?')) {
      return;
    }

    const name = prompt('Name des neuen Projekts:', 'Neues Projekt');
    if (name) {
      setProjectName(name);
      setBlocks([]);
      setConnections([]);
      setSystems([]);
      setPlans([]);
      setTitleBlockTemplates([]);
      setSelectedBlockIds([]);
      toast.success(`Neues Projekt "${name}" erstellt`);
    }
  }, [blocks.length]);

  // Delete selected blocks
  const handleDeleteSelected = useCallback(() => {
    if (selectedBlockIds.length === 0) return;

    // Get fresh values from refs (avoid stale closures)
    const currentBlocks = blocksRef.current;
    const currentConnections = connectionsRef.current;
    const currentSystems = systemsRef.current;

    const updatedBlocks = currentBlocks.filter(b => !selectedBlockIds.includes(b.id));
    const updatedConnections = currentConnections.filter(c => 
      !selectedBlockIds.includes(c.sourceBlockId) && 
      !selectedBlockIds.includes(c.targetBlockId)
    );
    
    const result = detectSystems(updatedBlocks, updatedConnections, currentSystems);

    setBlocks(result.blocks);
    setConnections(updatedConnections);
    setSystems(result.systems);
    setSelectedBlockIds([]);
    
    // ⚠️ LABELS ARE 100% PERSISTENT - Keep them even if blocks are deleted
    // User can manually delete labels if they want
    console.log('ℹ️ Keeping all labels (persistent mode)');

    toast.success(`${selectedBlockIds.length} Block(s) gelöscht`);
  }, [selectedBlockIds, blocks, connections, systems]);

  // Create subsystem from selection
  const handleCreateSubsystem = useCallback(() => {
    // Check if we have any selection
    const hasSelection = 
      selectedBlockIds.length > 0 || 
      selectedConnectionIds.length > 0 || 
      selectedLabelIds.length > 0 || 
      selectedTextIds.length > 0;

    if (!hasSelection) {
      toast.error('Bitte wählen Sie mindestens ein Element aus');
      return;
    }

    // Prompt for subsystem name
    const name = window.prompt('Subsystem-Name:');
    if (!name || name.trim() === '') {
      toast.info('Subsystem-Erstellung abgebrochen');
      return;
    }

    try {
      // Get selected elements
      const selectedBlocks = blocks.filter(b => selectedBlockIds.includes(b.id));
      const selectedConnections = connections.filter(c => selectedConnectionIds.includes(c.id));
      const selectedLabels = placedLabels.filter(l => selectedLabelIds.includes(l.id));
      const selectedTexts = textAnnotations.filter(t => selectedTextIds.includes(t.id));

      // Create subsystem
      const subsystem = createSubsystemFromSelection(
        selectedBlocks,
        selectedConnections,
        selectedLabels,
        selectedTexts,
        name.trim()
      );

      // Save to library
      saveSubsystemToLibrary(subsystem);

      // Clear selection
      setSelectedBlockIds([]);
      setSelectedConnectionIds([]);
      setSelectedLabelIds([]);
      setSelectedTextIds([]);

      toast.success(`Subsystem "${name}" wurde erstellt und in der Bibliothek gespeichert`);
      
      // Tutorial-Event emittieren
      emitTutorialEvent({ type: 'action-performed', action: 'subsystem-created' });
    } catch (error) {
      console.error('Error creating subsystem:', error);
      toast.error('Fehler beim Erstellen des Subsystems');
    }
  }, [
    selectedBlockIds, 
    selectedConnectionIds, 
    selectedLabelIds, 
    selectedTextIds,
    blocks,
    connections,
    placedLabels,
    textAnnotations
  ]);

  // Place subsystem helper function (used for both click and drag&drop)
  const handlePlaceSubsystem = useCallback((subsystem: Subsystem, position: IsoPoint) => {
    const result = placeSubsystem(subsystem, position);
    
    // Add all blocks
    result.blocks.forEach(block => {
      setBlocks(prev => [...prev, block]);
    });
    
    // Add all connections
    result.connections.forEach(conn => {
      setConnections(prev => [...prev, conn]);
    });
    
    // Add all labels
    result.labels.forEach(label => {
      setPlacedLabels(prev => [...prev, label]);
    });
    
    // Add all texts
    result.texts.forEach(text => {
      setTextAnnotations(prev => [...prev, text]);
    });
    
    toast.success(`Subsystem "${subsystem.name}" platziert`);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // NEW: Escape key cancels reassign mode
      if (e.key === 'Escape' && reassigningLabelId) {
        setReassigningLabelId(null);
        toast.info('Neuzuweisung abgebrochen');
        return;
      }

      // Rotation with "R"
      if (e.key.toLowerCase() === 'r' && selectedBlockIds.length > 0) {
        selectedBlockIds.forEach((blockId) => {
          const block = blocks.find((b) => b.id === blockId);
          if (block) {
            handleBlockUpdate(blockId, {
              transform: rotateTransform(block.transform),
            });
          }
        });
        toast.success('Block rotiert');
      }

      // Delete with Delete/Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedBlockIds.length > 0) {
          handleDeleteSelected();
        } else if (selectedTextIds.length > 0) {
          selectedTextIds.forEach(id => handleTextAnnotationDelete(id));
        } else if (selectedLabelIds.length > 0) {
          selectedLabelIds.forEach(id => handleLabelDelete(id));
        }
      }

      // Grid toggle with "G"
      if (e.key.toLowerCase() === 'g') {
        setBackgroundSettings((prev) => ({ ...prev, visible: !prev.visible }));
      }

      // Snap toggle with "S"
      if (e.key.toLowerCase() === 's') {
        setSnapEnabled((prev) => {
          const newValue = !prev;
          toast.info(newValue ? 'Snap aktiviert' : 'Snap deaktiviert');
          return newValue;
        });
      }

      // Zoom shortcuts
      if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      }
      if (e.key === '-') {
        handleZoomOut();
      }
      if (e.key === '0') {
        handleZoomReset();
      }

      // Ctrl/Cmd shortcuts
      if (e.ctrlKey || e.metaKey) {
        if (e.key.toLowerCase() === 'c') {
          e.preventDefault();
          // Copy blocks, texts, and labels (at least one type must be selected)
          if (selectedBlockIds.length > 0 || selectedTextIds.length > 0 || selectedLabelIds.length > 0 || selectedConnectionIds.length > 0) {
            // Collect all block IDs: explicitly selected + blocks referenced by selected connections
            const blockIdsToInclude = new Set(selectedBlockIds);
            selectedConnectionIds.forEach(connId => {
              const conn = connections.find(c => c.id === connId);
              if (conn) {
                blockIdsToInclude.add(conn.sourceBlockId);
                blockIdsToInclude.add(conn.targetBlockId);
              }
            });
            
            const selectedBlocks = blocks.filter((b) => blockIdsToInclude.has(b.id));
            
            // Find connections: between selected blocks OR explicitly selected
            const relevantConnections = connections.filter((conn) =>
              // Both blocks selected (including blocks added from connections)
              (blockIdsToInclude.has(conn.sourceBlockId) &&
               blockIdsToInclude.has(conn.targetBlockId)) ||
              // OR connection explicitly selected
              selectedConnectionIds.includes(conn.id)
            );
            
            // Check if all selected blocks belong to the same system
            const systemIds = new Set(selectedBlocks.map(b => b.systemId).filter(Boolean));
            let systemInfo: ClipboardData['systemInfo'] = undefined;
            
            if (systemIds.size === 1) {
              const systemId = Array.from(systemIds)[0];
              const system = systems.find(s => s.id === systemId);
              if (system) {
                // Check if all blocks of this system are selected
                const systemBlockIds = blocks.filter(b => b.systemId === systemId).map(b => b.id);
                const allSystemBlocksSelected = systemBlockIds.every(id => selectedBlockIds.includes(id));
                
                if (allSystemBlocksSelected) {
                  systemInfo = {
                    name: system.name,
                    color: system.color,
                    attributes: system.attributes,
                    description: system.description,
                  };
                  // Toast moved to end to show total count including texts and labels
                }
              }
            }
            
            // Deep copy blocks with ALL attributes
            const copiedBlocks = selectedBlocks.map(block => ({
              ...block,
              blockData: {
                ...block.blockData,
                attributes: block.blockData.attributes ? block.blockData.attributes.map(attr => ({ ...attr })) : undefined,
              },
            }));
            
            // Deep copy connections with ALL attributes
            const copiedConnections = relevantConnections.map(conn => ({
              ...conn,
              attributes: conn.attributes ? conn.attributes.map(attr => ({ ...attr })) : undefined,
            }));
            
            console.log('📋 COPY - Connections:', copiedConnections.length, copiedConnections.map(c => ({
              id: c.id,
              hasAttributes: !!c.attributes,
              attributeCount: c.attributes?.length || 0,
              attributes: c.attributes
            })));
            
            // Copy selected text annotations
            const selectedTexts = textAnnotations.filter((t) => selectedTextIds.includes(t.id));
            const copiedTexts = selectedTexts.map(text => ({ ...text }));
            
            // OPTION C: Smart Copy with validation for explicitly selected labels
            const copiedBlockIds = new Set(copiedBlocks.map(b => b.id));
            const copiedConnectionIds = new Set(copiedConnections.map(c => c.id));
            const copiedTextIds = new Set(copiedTexts.map(t => t.id));
            
            let skippedLabelCount = 0;
            
            const labelsAttachedToCopiedElements = placedLabels.filter((label) => {
              // Include label if it's attached to a copied element
              if (label.targetElementType === 'block' && copiedBlockIds.has(label.targetElementId)) return true;
              if (label.targetElementType === 'connection' && copiedConnectionIds.has(label.targetElementId)) return true;
              if (label.targetElementType === 'text-annotation' && copiedTextIds.has(label.targetElementId)) return true;
              
              // Explicitly selected labels: ONLY copy if target element exists and is NOT being copied
              if (selectedLabelIds.includes(label.id)) {
                // Check if target element exists in canvas
                const targetExists = 
                  (label.targetElementType === 'block' && blocks.some(b => b.id === label.targetElementId)) ||
                  (label.targetElementType === 'connection' && connections.some(c => c.id === label.targetElementId)) ||
                  (label.targetElementType === 'text-annotation' && textAnnotations.some(t => t.id === label.targetElementId));
                
                if (!targetExists) {
                  console.warn('⚠️ Label references non-existent element:', label.id, label.targetElementId);
                  skippedLabelCount++;
                  return false; // Skip corrupted labels
                }
                
                // Check if target is being copied (already handled above)
                const targetBeingCopied = 
                  (label.targetElementType === 'block' && copiedBlockIds.has(label.targetElementId)) ||
                  (label.targetElementType === 'connection' && copiedConnectionIds.has(label.targetElementId)) ||
                  (label.targetElementType === 'text-annotation' && copiedTextIds.has(label.targetElementId));
                
                if (targetBeingCopied) {
                  return true; // Already included above, but keep for clarity
                }
                
                // Label selected but target NOT copied → Skip with warning
                console.warn('⚠️ Label selected but target element not copied:', label.id, '→', label.targetElementId);
                skippedLabelCount++;
                return false;
              }
              
              return false;
            });
            
            const copiedLabels = labelsAttachedToCopiedElements.map(label => ({ ...label }));
            
            console.log('🏷️ COPY-DEBUG: Label breakdown:', {
              total: placedLabels.length,
              attached: labelsAttachedToCopiedElements.length,
              copied: copiedLabels.length,
              copiedBlockIds: Array.from(copiedBlockIds),
              copiedConnectionIds: Array.from(copiedConnectionIds),
              labelTargets: copiedLabels.map(l => ({ id: l.id, type: l.targetElementType, target: l.targetElementId }))
            });
            
            setClipboard({
              blocks: copiedBlocks,
              connections: copiedConnections,
              textAnnotations: copiedTexts,
              placedLabels: copiedLabels,
              systemInfo,
            });
            
            console.log('📋 COPY:', copiedBlocks.length, 'blocks,', copiedConnections.length, 'conns,', copiedTexts.length, 'texts,', copiedLabels.length, 'labels');
            
            // Show success message with optional warning
            const totalItems = copiedBlocks.length + copiedConnections.length + copiedTexts.length + copiedLabels.length;
            if (systemInfo) {
              toast.success(`System \"${systemInfo.name}\" mit ${totalItems} Element(en) kopiert`);
            } else {
              toast.success(`${totalItems} Element(e) kopiert`);
            }
            
            // Warn if labels were skipped
            if (skippedLabelCount > 0) {
              toast.warning(`${skippedLabelCount} Label(s) übersprungen (Ziel-Element nicht ausgewählt oder fehlt)`);
            }
          } else if (selectedConnectionIds.length > 0 && selectedBlockIds.length === 0) {
            // NEW: Copy connection attributes only (no blocks selected)
            const selectedConnections = connections.filter((c) => selectedConnectionIds.includes(c.id));
            
            // Collect all unique attributes from selected connections
            const attributeMap = new Map<string, PartAttribute>();
            selectedConnections.forEach(conn => {
              conn.attributes?.forEach(attr => {
                // Use name as key to avoid duplicates
                if (!attributeMap.has(attr.name)) {
                  attributeMap.set(attr.name, { ...attr });
                }
              });
            });
            
            const uniqueAttributes = Array.from(attributeMap.values());
            
            if (uniqueAttributes.length === 0) {
              toast.warning('Keine Attribute zum Kopieren vorhanden');
            } else {
              setClipboard({
                blocks: [],
                connections: [],
                textAnnotations: [],
                placedLabels: [],
                connectionAttributes: uniqueAttributes,
                sourceConnectionCount: selectedConnections.length,
              });
              
              console.log('📋 COPY CONN-ATTRS:', uniqueAttributes.length, 'attributes from', selectedConnections.length, 'connections');
              
              const attrNames = uniqueAttributes.map(a => a.name).join(', ');
              const preview = attrNames.length > 40 ? attrNames.substring(0, 37) + '...' : attrNames;
              toast.success(
                `${uniqueAttributes.length} Attribut(e) von ${selectedConnections.length} Verbindung(en) kopiert${uniqueAttributes.length <= 3 ? ': ' + preview : ''}`
              );
            }
          }
        }
        if (e.key.toLowerCase() === 'v') {
          e.preventDefault();
          if (clipboard.blocks.length > 0 || clipboard.textAnnotations.length > 0 || clipboard.placedLabels.length > 0) {
            // Create mapping from old IDs to new IDs
            const idMapping = new Map<string, string>();
            
            // Calculate paste offset relative to mouse position (center of clipboard content -> mouse position)
            let pasteOffset = { u: 1, v: 1 };
            
            if (mousePositionRef.current) {
              let sumU = 0, sumV = 0, count = 0;
              
              clipboard.blocks.forEach(b => { sumU += b.transform.position.u; sumV += b.transform.position.v; count++; });
              clipboard.textAnnotations.forEach(t => { sumU += t.position.u; sumV += t.position.v; count++; });
              clipboard.placedLabels.forEach(l => { sumU += l.position.u; sumV += l.position.v; count++; });
              
              if (count > 0) {
                const centroidU = sumU / count;
                const centroidV = sumV / count;
                
                // Snap to grid if enabled (assuming 1 unit grid)
                let targetU = mousePositionRef.current.u;
                let targetV = mousePositionRef.current.v;
                
                if (snapEnabled) {
                  targetU = Math.round(targetU);
                  targetV = Math.round(targetV);
                }
                
                pasteOffset = {
                  u: targetU - centroidU,
                  v: targetV - centroidV
                };
              }
            }

            // Create new system if copying a complete system
            let newSystemId: string | undefined = undefined;
            if (clipboard.systemInfo) {
              newSystemId = `system-${Date.now()}`;
              const newSystem: BlockSystem = {
                id: newSystemId,
                name: `${clipboard.systemInfo.name} (Kopie)`,
                color: clipboard.systemInfo.color,
                blockIds: [],
                connectionIds: [],
                attributes: clipboard.systemInfo.attributes ? [...clipboard.systemInfo.attributes] : undefined,
                description: clipboard.systemInfo.description,
              };
              setSystems((prev) => [...prev, newSystem]);
            }
            
            // Paste blocks with new IDs
            const pastedBlocks = clipboard.blocks.map((block) => {
              const newId = `block-${Date.now()}-${Math.random()}`;
              idMapping.set(block.id, newId);
              
              return {
                ...block,
                id: newId,
                transform: {
                  ...block.transform,
                  position: {
                    u: block.transform.position.u + pasteOffset.u,
                    v: block.transform.position.v + pasteOffset.v,
                  },
                },
                systemId: newSystemId, // Assign to new system or undefined
              };
            });

            // Paste connections with updated block IDs and create ID mapping
            const connectionIdMapping = new Map<string, string>();
            
            const pastedConnections = clipboard.connections.map((conn) => {
              const newId = `conn-${Date.now()}-${Math.random()}`;
              connectionIdMapping.set(conn.id, newId);
              return {
                ...conn,
                id: newId,
                sourceBlockId: idMapping.get(conn.sourceBlockId) || conn.sourceBlockId,
                targetBlockId: idMapping.get(conn.targetBlockId) || conn.targetBlockId,
                // ✅ FIX: Deep copy attributes to prevent shared references
                attributes: conn.attributes ? conn.attributes.map(attr => ({ ...attr })) : undefined,
              };
            });

            // Paste text annotations with new IDs and create ID mapping
            const textIdMapping = new Map<string, string>();
            const pastedTexts = clipboard.textAnnotations.map((text) => {
              const newId = `text-${Date.now()}-${Math.random()}`;
              textIdMapping.set(text.id, newId);
              return {
                ...text,
                id: newId,
                position: {
                  u: text.position.u + pasteOffset.u,
                  v: text.position.v + pasteOffset.v,
                },
              };
            });
            
            // Paste labels with new IDs and updated target references
            // NOTE: Connection labels are handled INSIDE setTimeout after auto-detection
            console.log('🏷️ PASTE-DEBUG: Clipboard has', clipboard.placedLabels.length, 'labels');
            
            // Only create pastedLabels for BLOCK and TEXT labels (connection labels handled in setTimeout)
            const pastedLabels = clipboard.placedLabels
              .filter((label) => {
                // Only include BLOCK and TEXT labels here (connection labels handled in setTimeout)
                if (label.targetElementType === 'block') {
                  const hasMapping = idMapping.has(label.targetElementId);
                  if (!hasMapping) {
                    console.log('❌ Label skipped (block not in mapping):', label.id, '→', label.targetElementId);
                  }
                  return hasMapping;
                } else if (label.targetElementType === 'text-annotation') {
                  const hasMapping = textIdMapping.has(label.targetElementId);
                  if (!hasMapping) {
                    console.log('❌ Label skipped (text not in mapping):', label.id, '→', label.targetElementId);
                  }
                  return hasMapping;
                }
                // Connection labels handled in setTimeout (after auto-detection)
                return false;
              })
              .map((label) => {
                const newId = `label-${Date.now()}-${Math.random()}`;
                
                // Update target element ID (we know it exists because of the filter above)
                let newTargetElementId = label.targetElementId;
                if (label.targetElementType === 'block') {
                  newTargetElementId = idMapping.get(label.targetElementId)!;
                } else if (label.targetElementType === 'text-annotation') {
                  newTargetElementId = textIdMapping.get(label.targetElementId)!;
                }
                
                return {
                  ...label,
                  id: newId,
                  targetElementId: newTargetElementId,
                  position: {
                    u: label.position.u + pasteOffset.u,
                    v: label.position.v + pasteOffset.v,
                  },
                };
              });

            console.log('📋 PASTE:', pastedBlocks.length, 'blocks,', pastedConnections.length, 'conns,', pastedTexts.length, 'texts,', pastedLabels.length, 'labels (block/text only)');
            
            // NEW STRATEGY: Let auto-detection create connections, then match and transfer attributes
            console.log('🔄 PASTE-STRATEGY: Auto-Detection erstellt Connections, dann Attribut-Transfer');
            
            // Add blocks first (this will trigger auto-detection)
            setBlocks((prev) => [...prev, ...pastedBlocks]);
            setTextAnnotations((prev) => [...prev, ...pastedTexts]);
            
            // Wait for auto-detection to create connections, then match and transfer attributes
            setTimeout(() => {
              setConnections((currentConnections) => {
                console.log('🔍 MATCHING: Clipboard-Connections → Auto-detected Connections');
                
                // Build a map of socket-pairs → clipboard connection attributes
                // Use connectedSockets instead of deprecated sourceBlockId/targetBlockId
                const clipboardConnectionMap = new Map<string, typeof clipboard.connections[0]>();
                clipboard.connections.forEach((conn) => {
                  // Map old block IDs to new block IDs in connectedSockets
                  const remappedSockets = conn.connectedSockets.map(socket => ({
                    ...socket,
                    blockId: idMapping.get(socket.blockId) || socket.blockId
                  }));
                  
                  // Create normalized key from all connected sockets (sorted for bidirectional matching)
                  const createSocketKey = (sockets: typeof remappedSockets) => {
                    return sockets
                      .map(s => `${s.blockId}:${s.socketIndex}:${s.lineIndex}:${s.isStart}`)
                      .sort()
                      .join('|');
                  };
                  
                  const key = createSocketKey(remappedSockets);
                  clipboardConnectionMap.set(key, conn);
                  console.log('📋 Clipboard-Connection:', key, '→', conn.attributes?.length || 0, 'attributes');
                });
                
                console.log('📋 Clipboard-Connection-Map Größe:', clipboardConnectionMap.size, 'Connections');
                
                // Find auto-detected connections and transfer attributes
                const updatedConnections = currentConnections.map((conn) => {
                  const createSocketKey = (sockets: typeof conn.connectedSockets) => {
                    return sockets
                      .map(s => `${s.blockId}:${s.socketIndex}:${s.lineIndex}:${s.isStart}`)
                      .sort()
                      .join('|');
                  };
                  
                  const key = createSocketKey(conn.connectedSockets);
                  const clipboardConn = clipboardConnectionMap.get(key);
                  
                  if (clipboardConn) {
                    console.log('✅ MATCH:', conn.id, '←', clipboardConn.id);
                    console.log('   Socket-Key:', key);
                    console.log('   Übertrage:', clipboardConn.attributes?.length || 0, 'Attribute');
                    
                    // Store mapping for label remapping
                    connectionIdMapping.set(clipboardConn.id, conn.id);
                    
                    // Transfer attributes (create new IDs to avoid conflicts)
                    const transferredAttributes = clipboardConn.attributes?.map(attr => ({
                      ...attr,
                      id: `attr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    })) || [];
                    
                    return {
                      ...conn,
                      attributes: transferredAttributes.length > 0 ? transferredAttributes : conn.attributes,
                    };
                  }
                  
                  return conn;
                });
                
                console.log('🔗 ConnectionIdMapping Größe:', connectionIdMapping.size);
                console.log('🔗 Mappings:', Array.from(connectionIdMapping.entries()).map(([old, newId]) => ({ old, new: newId })));
                
                // DEBUG: Log all auto-detected connections for comparison
                console.log('🔍 Auto-detected connections:', currentConnections.length);
                currentConnections.forEach((conn, i) => {
                  const key = conn.connectedSockets
                    .map(s => `${s.blockId}:${s.socketIndex}:${s.lineIndex}:${s.isStart}`)
                    .sort()
                    .join('|');
                  console.log(`  [${i}]`, conn.id, '→', key, `(${conn.attributes?.length || 0} attrs)`);
                });
                
                // Now paste ALL labels: Block/Text labels (already created) + Connection labels (create now)
                // Create connection labels OUTSIDE setPlacedLabels so we can select them
                const connectionLabels = clipboard.placedLabels
                  .filter(label => label.targetElementType === 'connection')
                  .map(label => {
                    const newTargetId = connectionIdMapping.get(label.targetElementId);
                    const newId = `label-${Date.now()}-${Math.random()}`;
                    
                    if (newTargetId) {
                      console.log('🏷️ LABEL-CREATE (connection):', newId, 'target:', label.targetElementId, '→', newTargetId);
                      return {
                        ...label,
                        id: newId,
                        targetElementId: newTargetId,
                        position: {
                          u: label.position.u + pasteOffset.u,
                          v: label.position.v + pasteOffset.v,
                        },
                      };
                    } else {
                      // WICHTIG: Verwaiste Labels BEHALTEN (können später manuell verbunden werden)
                      console.warn('⚠️ LABEL-ORPHANED (connection):', newId, 'kein Match für', label.targetElementId);
                      return {
                        ...label,
                        id: newId,
                        position: {
                          u: label.position.u + pasteOffset.u,
                          v: label.position.v + pasteOffset.v,
                        },
                      };
                    }
                  });
                
                const allNewLabels = [...pastedLabels, ...connectionLabels];
                
                setPlacedLabels((currentLabels) => {
                  console.log('🏷️ Eingefügt:', allNewLabels.length, 'Labels (', pastedLabels.length, 'block/text +', connectionLabels.length, 'connection)');
                  return [...currentLabels, ...allNewLabels];
                });
                
                // Update system with matched connection IDs
                if (newSystemId) {
                  const newConnectionIds = Array.from(connectionIdMapping.values());
                  setSystems((prev) =>
                    prev.map((s) =>
                      s.id === newSystemId
                        ? { ...s, blockIds: pastedBlocks.map((b) => b.id), connectionIds: newConnectionIds }
                        : s
                    )
                  );
                  console.log('🔗 System-Update:', newSystemId, 'mit', newConnectionIds.length, 'Connections');
                }
                
                // Select matched connections and connection labels
                const newConnectionIds = Array.from(connectionIdMapping.values());
                setSelectedConnectionIds(newConnectionIds);
                
                // Add connection labels to selection (block/text labels already selected)
                setSelectedLabelIds(prev => [...prev, ...connectionLabels.map(l => l.id)]);
                
                return updatedConnections;
              });
              
              // Show success toast after matching complete
              setTimeout(() => {
                toast.success(clipboard.systemInfo 
                  ? `System \"${clipboard.systemInfo.name}\" mit ${pastedBlocks.length} Blöcken und ${clipboard.connections.length} Verbindungen eingefügt`
                  : `${pastedBlocks.length} Block${pastedBlocks.length === 1 ? '' : 's'} eingefügt`
                );
              }, 50);
            }, 150); // Wait for auto-detection to complete
            
            // Select blocks and texts immediately (connections+labels selected after matching)
            setSelectedBlockIds(pastedBlocks.map((b) => b.id));
            setSelectedTextIds(pastedTexts.map((t) => t.id));
            setSelectedLabelIds(pastedLabels.map((l) => l.id)); // Select block/text labels immediately
          } else if (clipboard.connectionAttributes && clipboard.connectionAttributes.length > 0) {
            // NEW: Paste connection attributes only
            if (selectedConnectionIds.length === 0) {
              toast.warning('Wähle Ziel-Verbindungen aus (Strg+V um Attribute einzufügen)');
            } else {
              const targetConnections = connections.filter((c) => selectedConnectionIds.includes(c.id));
              
              if (targetConnections.length === 0) {
                toast.error('Keine Verbindungen ausgewählt');
                return;
              }
              
              let totalAttributesAdded = 0;
              let totalAttributesSkipped = 0;
              
              // Apply attributes to each selected connection
              setConnections((prev) =>
                prev.map((c) => {
                  if (!selectedConnectionIds.includes(c.id)) return c;
                  
                  // Merge with existing attributes (avoid duplicates by name)
                  const existingAttrNames = new Set((c.attributes || []).map((a) => a.name));
                  const newAttributes = (clipboard.connectionAttributes || []).map((attr) => ({
                    ...attr,
                    id: `attr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  })).filter((attr) => {
                    if (existingAttrNames.has(attr.name)) {
                      totalAttributesSkipped++;
                      return false;
                    }
                    totalAttributesAdded++;
                    return true;
                  });
                  
                  return {
                    ...c,
                    attributes: [...(c.attributes || []), ...newAttributes],
                  };
                })
              );
              
              // Success message
              const attrCount = clipboard.connectionAttributes.length;
              const connCount = targetConnections.length;
              
              if (totalAttributesSkipped > 0) {
                toast.success(
                  `${totalAttributesAdded} Attribut(e) auf ${connCount} Verbindung(en) eingefügt (${totalAttributesSkipped} übersprungen - bereits vorhanden)`
                );
              } else {
                toast.success(
                  `${attrCount} Attribut(e) auf ${connCount} Verbindung(en) eingefügt`
                );
              }
              
              console.log('📋 PASTE CONN-ATTRS:', totalAttributesAdded, 'added to', connCount, 'connections,', totalAttributesSkipped, 'skipped');
            }
          }
        }
        if (e.key.toLowerCase() === 's') {
          e.preventDefault();
          handleSave();
        }
        if (e.key.toLowerCase() === 'o') {
          e.preventDefault();
          handleLoad();
        }
        if (e.key.toLowerCase() === 'n') {
          e.preventDefault();
          handleNewProject();
        }
      }

      // Escape to deselect
      if (e.key === 'Escape') {
        if (activeTool === 'pipe-draw') {
          // Cancel pipe drawing
          setActiveTool('select');
          setPipeDrawStart(null);
          setPipeDrawPreview(null);
          toast.info('Leitung-Zeichnen abgebrochen');
        } else if (activeTool === 'text') {
          // Cancel text tool
          setActiveTool('select');
          toast.info('Text-Tool abgebrochen');
        } else if (activeTool === 'label') {
          // Cancel label tool
          setActiveTool('select');
          setSelectedLabelTemplateId(null);
          toast.info('Beschriftungs-Tool abgebrochen');
        } else {
          setSelectedBlockIds([]);
          setSelectedTextIds([]);
          setSelectedLabelIds([]);
          setSelectedConnectionIds([]);
        }
      }

      // Activate pipe tool with "L"
      if (e.key.toLowerCase() === 'l') {
        if (activeTool === 'pipe-draw') {
          setActiveTool('select');
          setPipeDrawStart(null);
          setPipeDrawPreview(null);
          toast.info('Leitung-Zeichnen deaktiviert');
        } else {
          setActiveTool('pipe-draw');
          toast.info('Leitung-Zeichnen aktiviert - Klicke für Startpunkt');
        }
      }

      // Activate text tool with "T"
      if (e.key.toLowerCase() === 't') {
        if (activeTool === 'text') {
          setActiveTool('select');
          toast.info('Text-Tool deaktiviert');
        } else {
          setActiveTool('text');
          toast.info('Text-Tool aktiviert - Klicke um Text zu platzieren');
        }
      }

      // Undo with Ctrl+Z
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }

      // Redo with Ctrl+Shift+Z or Ctrl+Y
      if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key.toLowerCase() === 'z' || e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedBlockIds,
    selectedTextIds,
    selectedLabelIds,
    blocks,
    reassigningLabelId,
    handleBlockUpdate,
    handleDeleteSelected,
    handleTextAnnotationDelete,
    handleLabelDelete,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleSave,
    handleLoad,
    handleNewProject,
    handleUndo,
    handleRedo,
    activeTool,
    clipboard,
  ]);

  const selectedBlocks = blocks.filter((b) => selectedBlockIds.includes(b.id));

  // Memoize selected connections to avoid re-filtering on every render
  const selectedConnections = useMemo(
    () => connections.filter(c => selectedConnectionIds.includes(c.id)),
    [connections, selectedConnectionIds]
  );

  // Systems state tracking (debug removed)

  // Calculate icon size based on multiplier (base size: 20px)
  const iconSize = Math.round(20 * (viewDisplaySettings.iconSizeMultiplier ?? 1.0));

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".isomproject"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Toolbar */}
      <div
        className="px-4 py-2 flex items-center gap-2 border-b overflow-x-auto no-scrollbar"
        style={{
          backgroundColor: 'var(--md-surface-container-low)',
          borderColor: 'var(--md-outline-variant)',
        }}
        data-tutorial="bs-toolbar"
      >
        {/* Hamburger-Menü */}
        <Button variant="ghost" size="default" onClick={() => setDrawerOpen(true)} title={t.editor.actions.openMenu}>
          <Menu style={{ width: iconSize, height: iconSize }} />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Undo/Redo */}
        <Button 
          variant="ghost" 
          size="default" 
          onClick={handleUndo} 
          disabled={historyIndex <= 0}
          title="Rückgängig (Strg+Z)"
        >
          <Undo2 style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button 
          variant="ghost" 
          size="default" 
          onClick={handleRedo} 
          disabled={historyIndex >= history.length - 1}
          title="Wiederholen (Strg+Y)"
        >
          <Redo2 style={{ width: iconSize, height: iconSize }} />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Seitenleiste Ein/Aus */}
        <Button 
          variant="ghost" 
          size="default" 
          onClick={() => setShowSidebar(!showSidebar)} 
          title={showSidebar ? "Seitenleiste ausblenden" : "Seitenleiste anzeigen"}
        >
          <PanelLeft style={{ width: iconSize, height: iconSize }} />
        </Button>
        
        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Ansicht & Snapping */}
        <Button
          variant="ghost"
          size="default"
          onClick={() => setBackgroundSettings((prev) => ({ ...prev, visible: !prev.visible }))}
          title={`${t.editor.tools.grid} (G)`}
          style={{
            color: backgroundSettings.visible ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
        >
          <Grid style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button
          variant="ghost"
          size="default"
          onClick={() => {
            setSnapEnabled((prev) => {
              const newValue = !prev;
              toast.info(newValue ? 'Snap aktiviert' : 'Snap deaktiviert');
              return newValue;
            });
          }}
          title={`${t.editor.tools.magnet} (S)`}
          style={{
            color: snapEnabled ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
        >
          <Magnet style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button
          variant="ghost"
          size="default"
          onClick={() => setViewDisplaySettings((prev) => ({ 
            ...prev, 
            showLabels: prev.showLabels === undefined ? false : !prev.showLabels 
          }))}
          title={t.editor.tools.tag}
          style={{
            color: (viewDisplaySettings.showLabels ?? true) ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
        >
          <Tag style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button
          variant="ghost"
          size="default"
          onClick={() => setShowDimensions((prev) => !prev)}
          title={t.editor.tools.ruler}
          style={{
            color: showDimensions ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
        >
          <Ruler style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button
          variant="ghost"
          size="default"
          onClick={() => {
            setShowCoordinateSystem((prev) => !prev);
            toast.info(showCoordinateSystem ? 'Koordinatensystem ausgeblendet' : 'Koordinatensystem eingeblendet');
          }}
          title="XYZ-Koordinatensystem ein/ausblenden"
          style={{
            color: showCoordinateSystem ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
        >
          <Axis3d style={{ width: iconSize, height: iconSize }} />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Werkzeuge */}
        <Button
          variant="ghost"
          size="default"
          onClick={() => {
            if (activeTool === 'pipe-draw') {
              setActiveTool('select');
              setPipeDrawStart(null);
              setPipeDrawPreview(null);
              toast.info('Leitung-Zeichnen deaktiviert');
            } else {
              setActiveTool('pipe-draw');
              toast.info('Leitung-Zeichnen aktiviert - Klicke für Startpunkt');
            }
          }}
          title={`${t.editor.tools.cable} (L)`}
          style={{
            color: activeTool === 'pipe-draw' ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
          data-tutorial="tool-pipe-draw"
        >
          <Cable style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button
          variant="ghost"
          size="default"
          onClick={() => {
            if (activeTool === 'text') {
              setActiveTool('select');
              toast.info('Text-Tool deaktiviert');
            } else {
              setActiveTool('text');
              toast.info('Text-Tool aktiviert - Klicke um Text zu platzieren');
            }
          }}
          title={`${t.editor.tools.text} (T)`}
          style={{
            color: activeTool === 'text' ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
          data-tutorial="tool-bs-text"
        >
          <Type style={{ width: iconSize, height: iconSize }} />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Funktionen */}
        <Button
          variant="ghost"
          size="default"
          onClick={() => {
            setFilterConnectionsOnly(!filterConnectionsOnly);
            if (!filterConnectionsOnly) {
              toast.info('Verbindungsanzeige aktiviert');
            } else {
              toast.info('Verbindungsanzeige deaktiviert');
            }
          }}
          title={t.editor.tools.link}
          style={{
            color: filterConnectionsOnly ? 'var(--md-primary)' : 'var(--md-on-surface-variant)',
          }}
        >
          <Link style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button
          variant="ghost"
          size="default"
          onClick={handleCreateSubsystem}
          title="Subsystem aus Auswahl erstellen"
          disabled={
            selectedBlockIds.length === 0 && 
            selectedConnectionIds.length === 0 && 
            selectedLabelIds.length === 0 && 
            selectedTextIds.length === 0
          }
          data-tutorial="create-subsystem-btn"
        >
          <Layers style={{ width: iconSize, height: iconSize }} />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Verwaltung */}
        <Button
          variant="ghost"
          size="default"
          onClick={() => setShowParameterManagement(true)}
          title={t.editor.tools.database}
          data-tutorial="parameter-mgmt-btn"
        >
          <Database style={{ width: iconSize, height: iconSize }} />
        </Button>

        <Button
          variant="ghost"
          size="default"
          onClick={() => setShowViewSettings(true)}
          title={t.menu.settings}
          data-tutorial="view-settings-btn"
        >
          <Settings style={{ width: iconSize, height: iconSize }} />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Auswahlfilter */}
        <SelectionFilterDropdown 
          filters={selectionFilters}
          onFiltersChange={setSelectionFilters}
        />

        {/* Alles löschen */}
        <Button 
          variant="ghost" 
          size="default" 
          onClick={handleClearAll} 
          title={t.common.delete}
          disabled={blocks.length === 0 && textAnnotations.length === 0}
        >
          <Trash2 style={{ width: iconSize, height: iconSize }} />
        </Button>

        <div className="flex-1" />

        {/* Tutorial Help Button */}
        <TutorialHelpButton currentModule="block-system" variant="icon" />

        {/* Projektname */}
        <div className="text-sm font-medium">{projectName}</div>
      </div>

      {/* Pipe Settings Bar - only visible when pipe-draw tool is active */}
      {activeTool === 'pipe-draw' && (
        <div
          className="px-4 py-2 flex items-center gap-4 border-b text-sm"
          style={{
            backgroundColor: 'var(--md-surface-container)',
            borderColor: 'var(--md-outline-variant)',
            color: 'var(--md-on-surface)',
          }}
        >
          <span className="font-medium">Leitungstyp:</span>
          
          {/* Pipe Type Selector */}
          <div className="flex items-center gap-2">
            <select
              value={selectedPipeTypeId}
              onChange={(e) => setSelectedPipeTypeId(e.target.value)}
              className="px-2 py-1 rounded border text-sm min-w-[180px]"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            >
              {pipeTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSavePipeType}
              title="Aktuelle Einstellungen als neuen Typ speichern"
              className="text-xs"
            >
              Speichern
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeletePipeType}
              title="Ausgewählten Typ löschen"
              className="text-xs"
              disabled={selectedPipeTypeId === 'default'}
            >
              Löschen
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRenamePipeType}
              title="Ausgewählten Typ umbenennen"
              className="text-xs"
              disabled={selectedPipeTypeId === 'default'}
            >
              Umbenennen
            </Button>
          </div>

          <div className="h-6 w-px bg-gray-300 mx-1" />
          
          <div className="flex items-center gap-2">
            <label className="text-xs">DN:</label>
            <select
              value={pipeDN}
              onChange={(e) => setPipeDN(Number(e.target.value))}
              className="px-2 py-1 rounded border text-sm"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            >
              <option value={15}>DN 15</option>
              <option value={20}>DN 20</option>
              <option value={25}>DN 25</option>
              <option value={32}>DN 32</option>
              <option value={40}>DN 40</option>
              <option value={50}>DN 50</option>
              <option value={65}>DN 65</option>
              <option value={80}>DN 80</option>
              <option value={100}>DN 100</option>
              <option value={125}>DN 125</option>
              <option value={150}>DN 150</option>
              <option value={200}>DN 200</option>
              <option value={250}>DN 250</option>
              <option value={300}>DN 300</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs">Linienstärke:</label>
            <input
              type="number"
              value={pipeLineWidth}
              onChange={(e) => setPipeLineWidth(Number(e.target.value))}
              min={0.5}
              max={10}
              step={0.5}
              className="w-16 px-2 py-1 rounded border text-sm"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            />
            <span className="text-xs text-gray-500">px</span>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs">Farbe:</label>
            <input
              type="color"
              value={pipeColor}
              onChange={(e) => setPipeColor(e.target.value)}
              className="w-8 h-8 rounded border cursor-pointer"
              style={{
                borderColor: 'var(--md-outline)',
              }}
            />
            <input
              type="text"
              value={pipeColor}
              onChange={(e) => setPipeColor(e.target.value)}
              className="w-20 px-2 py-1 rounded border text-xs font-mono"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs">Linienart:</label>
            <select
              value={pipeLineStyle}
              onChange={(e) => setPipeLineStyle(e.target.value as 'solid' | 'dashed' | 'dotted' | 'dashdot')}
              className="px-2 py-1 rounded border text-sm min-w-[120px]"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            >
              <option value="solid">Durchgehend</option>
              <option value="dashed">Strichliert</option>
              <option value="dotted">Punktiert</option>
              <option value="dashdot">Strichpunktiert</option>
            </select>
          </div>
        </div>
      )}

      {/* Main content */}
      <ResizableLayout
        showLeftSidebar={showSidebar}
        leftSidebar={
          <BlockSystemSidebar
              onBlockSelect={(block) => {
            // Block selection is handled by canvas drag-and-drop
            console.log('Block selected from library:', block);
          }}
          onSubsystemSelect={(subsystem) => {
            // Place subsystem at viewport center
            const centerPosition: IsoPoint = {
              u: -viewState.panX / viewState.zoom,
              v: -viewState.panY / viewState.zoom,
            };
            
            const result = placeSubsystem(subsystem, centerPosition);
            
            // Add all blocks
            result.blocks.forEach(block => {
              setBlocks(prev => [...prev, block]);
            });
            
            // Add all connections
            result.connections.forEach(conn => {
              setConnections(prev => [...prev, conn]);
            });
            
            // Add all labels
            result.labels.forEach(label => {
              setPlacedLabels(prev => [...prev, label]);
            });
            
            // Add all texts
            result.texts.forEach(text => {
              setTextAnnotations(prev => [...prev, text]);
            });
            
            toast.success(`Subsystem "${subsystem.name}" platziert`);
          }}
          pipeTypes={pipeTypes}
          labelTemplates={labelTemplates}
          onLabelTemplateAdd={handleLabelTemplateAdd}
          onLabelTemplateUpdate={handleLabelTemplateUpdate}
          onLabelTemplateDelete={handleLabelTemplateDelete}
          onLabelTemplateSelect={handleLabelTemplateSelect}
          selectedLabelTemplateId={selectedLabelTemplateId}
          blocks={blocks}
          connections={connections}
          textAnnotations={textAnnotations}
          rootFolder={rootFolder}
          activeDrawingId={activeDrawingId}
          activePlanId={activePlanId}
          onDrawingSelect={(id) => {
            handleDrawingSelect(id);
            setActivePlanId(undefined); // Reset plan selection when switching drawing
          }}
          onDrawingCreate={handleDrawingCreate}
          onDrawingRename={handleDrawingRename}
          onDrawingDelete={handleDrawingDelete}
          onDrawingDuplicate={handleDrawingDuplicate}
          onFolderCreate={handleFolderCreate}
          onFolderRename={handleFolderRename}
          onFolderDelete={handleFolderDelete}
          onFolderToggle={handleFolderToggle}
          onItemMove={handleItemMove}
          activeTab={sidebarTab}
          onTabChange={setSidebarTab}
          onPlanSelect={handlePlanSelect}
          onPlanCreate={(drawingId) => {
             if (drawingId === activeDrawingId) {
               handleCreatePlan(drawingId);
             } else {
               handleDrawingSelect(drawingId);
               setTimeout(() => handleCreatePlan(drawingId), 100);
             }
          }}
          onPlanDelete={handlePlanDeleteFromBrowser}
        />
        }
        mainContent={
          activePlanId ? (
            <PlanLayoutEditor 
              plan={plans.find(p => p.id === activePlanId)!}
              blocks={blocks}
              connections={connections}
              textAnnotations={textAnnotations}
              placedLabels={placedLabels}
              labelTemplates={labelTemplates}
              systems={systems}
              systemColors={systemColors}
              backgroundSettings={activeBackgroundSettings}
              pipeTypes={pipeTypes}
              viewDisplaySettings={viewDisplaySettings}
              connectionMarkers={connectionMarkers}
              projectName={projectName}
              onUpdatePlan={handleUpdatePlan}
              onClose={() => setActivePlanId(undefined)}
              projectTemplates={titleBlockTemplates}
              onSaveProjectTemplate={(t) => setTitleBlockTemplates(prev => {
                  const idx = prev.findIndex(existing => existing.id === t.id);
                  if (idx >= 0) {
                      const copy = [...prev];
                      copy[idx] = t;
                      return copy;
                  }
                  return [...prev, t];
              })}
              onDeleteProjectTemplate={(id) => setTitleBlockTemplates(prev => prev.filter(t => t.id !== id))}
            />
          ) : (
            <BlockSystemCanvas
            blocks={blocks}
            connections={connections}
            connectionMarkers={connectionMarkers}
            selectedBlockIds={selectedBlockIds}
            selectedConnectionIds={selectedConnectionIds}
            selectedMarkerIds={selectedMarkerIds}
            textAnnotations={textAnnotations}
            selectedTextIds={selectedTextIds}
            labelTemplates={labelTemplates}
            placedLabels={placedLabels}
            selectedLabelIds={selectedLabelIds}
            selectedLabelTemplateId={selectedLabelTemplateId}
            systems={systems}
            pipeTypes={pipeTypes}
            projectName={projectName}
            viewState={viewState}
            backgroundSettings={activeBackgroundSettings}
            viewDisplaySettings={viewDisplaySettings}
            onViewStateChange={setViewState}
            onBlockAdd={handleBlockAdd}
            onBlockUpdate={handleBlockUpdate}
            onBlockSelect={handleBlockSelect}
            onConnectionSelect={handleConnectionSelect}
            onMarkerSelect={setSelectedMarkerIds}
            onBlockContextMenu={handleBlockContextMenu}
            snapEnabled={snapEnabled}
            filterConnectionsOnly={filterConnectionsOnly}
            selectionFilters={selectionFilters}
            onTextSelect={setSelectedTextIds}
            onTextClick={(position) => {
              setTextPlacementPosition(position);
              setEditingTextAnnotation(null);
              setShowTextDialog(true);
            }}
            onTextEdit={(textId) => {
              const annotation = textAnnotations.find(t => t.id === textId);
              if (annotation) {
                setEditingTextAnnotation(annotation);
                setTextPlacementPosition(null);
                setShowTextDialog(true);
              }
            }}
            onTextUpdate={(textId, updates) => {
              setTextAnnotations(prev => 
                prev.map(t => t.id === textId ? { ...t, ...updates } : t)
              );
            }}
            onLabelSelect={setSelectedLabelIds}
            onLabelAdd={handleLabelAdd}
            onLabelUpdate={handleLabelUpdate}
            onLabelEdit={handleLabelEdit}
            onConnectionCreate={handleConnectionCreateInternal}
            onBlockAddWithConnection={handleBlockAddWithConnection}
            onSubsystemPlace={handlePlaceSubsystem}
            onDimensionClick={handleDimensionClick}
            systemColors={systemColors}
            showDimensions={showDimensions}
            showCoordinateSystem={showCoordinateSystem}
            activeTool={activeTool}
            pipeDrawStart={pipeDrawStart}
            pipeDrawPreview={pipeDrawPreview}
            onPipeDrawStart={setPipeDrawStart}
            onPipeDrawPreview={setPipeDrawPreview}
            onMouseMove={(point) => {
              mousePositionRef.current = point;
            }}
            onPipeDrawEnd={(endPoint) => {
              // Create pipe directly with default values - endPoint is passed as parameter
              if (pipeDrawStart) {
                console.log('📍 Creating pipe from', pipeDrawStart, 'to', endPoint);
                
                // Calculate line length
                const du = endPoint.u - pipeDrawStart.u;
                const dv = endPoint.v - pipeDrawStart.v;
                
                console.log('📏 Line delta:', { du, dv });
                
                // Default values
                const defaultDN = pipeDN;
                const defaultColor = pipeColor;
                const defaultLineWidth = pipeLineWidth;

                // Calculate actual length in grid units
                const actualLength = Math.sqrt(du * du + dv * dv);
                
                // Calculate length in mm (grid units * mm per grid unit)
                const gridSizeMm = backgroundSettings.isometricGridSize;
                const lengthInMm = actualLength * gridSizeMm;

                // Create pipe elements: Line + 2 Sockets + Dimension
                const elements = [
                  // Main line (local coordinates from 0,0 to end)
                  {
                    id: `line-${Date.now()}`,
                    type: 'line' as const,
                    start: { u: 0, v: 0 },
                    end: { u: du, v: dv },
                    color: defaultColor,
                    width: defaultLineWidth,
                    lineStyle: pipeLineStyle,
                  },
                  // Start socket at origin
                  {
                    id: `socket-start-${Date.now()}`,
                    type: 'connection' as const,
                    position: { u: 0, v: 0 },
                    color: defaultColor,
                    fillType: 'ring' as const,
                    diameter: 50,
                    strokeWidth: 5,
                    label: '',
                  },
                  // End socket at relative end position
                  {
                    id: `socket-end-${Date.now() + 1}`,
                    type: 'connection' as const,
                    position: { u: du, v: dv },
                    color: defaultColor,
                    fillType: 'ring' as const,
                    diameter: 50,
                    strokeWidth: 5,
                    label: '',
                  },
                  // Automatic dimension
                  {
                    id: `dimension-${Date.now()}`,
                    type: 'dimension' as const,
                    start: { u: 0, v: 0 },
                    end: { u: du, v: dv },
                    value: lengthInMm.toFixed(1),
                    unit: 'mm',
                    showUnit: false,
                    decimalPlaces: 0,
                    offset: 0.5,
                    color: '#000000',
                    textSize: 50,
                    lineWidth: 2.5,
                    showLine: false,
                    showArrows: false,
                    arrowSize: 8,
                    arrowFilled: false,
                    arrowPosition: 'inside' as const,
                  },
                ];

                // Create pipe block
                const pipe: PlacedBlock = {
                  id: `pipe-${Date.now()}`,
                  blockData: {
                    name: `Leitung DN${defaultDN}`,
                    elements: elements,
                    attributes: [
                      {
                        id: `attr-dn-${Date.now()}`,
                        name: 'DN',
                        value: defaultDN,
                        unit: 'mm',
                        type: 'TGA',
                      },
                      {
                        id: `attr-length-${Date.now()}`,
                        name: 'Länge',
                        value: Number(actualLength.toFixed(2)),
                        unit: 'Grid',
                        type: 'number',
                      },
                    ],
                  },
                  transform: {
                    position: pipeDrawStart,
                    rotation: 0,
                    scale: 1.0,
                    mirrorX: false,
                    mirrorY: false,
                  },
                };

                console.log('🔧 Created pipe:', pipe);
                console.log('   Transform:', pipe.transform);
                console.log('   Elements:', pipe.blockData.elements);
                
                // Check for socket matches with existing blocks to create automatic connections
                const newConnections: SocketConnection[] = [];
                const startSocketIndex = 1; // socket-start element
                const endSocketIndex = 2; // socket-end element
                
                // Get current blocks from ref (avoid stale closure)
                const currentBlocks = blocksRef.current;
                
                // Helper to get transformed socket position
                const getTransformedSocketPos = (block: PlacedBlock, socketElementIndex: number): { u: number, v: number } => {
                  const socketElement = block.blockData.elements[socketElementIndex];
                  if (socketElement.type !== 'connection') return { u: 0, v: 0 };
                  return {
                    u: block.transform.position.u + socketElement.position.u,
                    v: block.transform.position.v + socketElement.position.v,
                  };
                };
                
                // Check start socket (at pipeDrawStart)
                currentBlocks.forEach((existingBlock) => {
                  existingBlock.blockData.elements.forEach((element, elementIndex) => {
                    if (element.type === 'connection') {
                      const existingSocketPos = getTransformedSocketPos(existingBlock, elementIndex);
                      const newStartSocketPos = { 
                        u: pipe.transform.position.u + elements[startSocketIndex].position.u,
                        v: pipe.transform.position.v + elements[startSocketIndex].position.v
                      };
                      
                      // Check if positions match (within tolerance)
                      const tolerance = 0.01;
                      const distance = Math.sqrt(
                        Math.pow(existingSocketPos.u - newStartSocketPos.u, 2) +
                        Math.pow(existingSocketPos.v - newStartSocketPos.v, 2)
                      );
                      
                      if (distance < tolerance) {
                        console.log('🔌 Found socket match at START:', {
                          existing: existingBlock.id,
                          new: pipe.id,
                          position: newStartSocketPos
                        });
                        
                        const connection: SocketConnection = {
                          id: `conn-${Date.now()}-${Math.random()}`,
                          sourceBlockId: existingBlock.id,
                          sourceSocketIndex: elementIndex,
                          targetBlockId: pipe.id,
                          targetSocketIndex: startSocketIndex,
                        };
                        newConnections.push(connection);
                      }
                    }
                  });
                });
                
                // Check end socket (at endPoint)
                currentBlocks.forEach((existingBlock) => {
                  existingBlock.blockData.elements.forEach((element, elementIndex) => {
                    if (element.type === 'connection') {
                      const existingSocketPos = getTransformedSocketPos(existingBlock, elementIndex);
                      const newEndSocketPos = {
                        u: pipe.transform.position.u + elements[endSocketIndex].position.u,
                        v: pipe.transform.position.v + elements[endSocketIndex].position.v
                      };
                      
                      // Check if positions match (within tolerance)
                      const tolerance = 0.01;
                      const distance = Math.sqrt(
                        Math.pow(existingSocketPos.u - newEndSocketPos.u, 2) +
                        Math.pow(existingSocketPos.v - newEndSocketPos.v, 2)
                      );
                      
                      if (distance < tolerance) {
                        console.log('🔌 Found socket match at END:', {
                          existing: existingBlock.id,
                          new: pipe.id,
                          position: newEndSocketPos
                        });
                        
                        const connection: SocketConnection = {
                          id: `conn-${Date.now()}-${Math.random()}`,
                          sourceBlockId: existingBlock.id,
                          sourceSocketIndex: elementIndex,
                          targetBlockId: pipe.id,
                          targetSocketIndex: endSocketIndex,
                        };
                        newConnections.push(connection);
                      }
                    }
                  });
                });
                
                console.log('🔌 Total connections found:', newConnections.length);
                
                if (newConnections.length > 0) {
                  // Use refs to get fresh state (avoid stale closures)
                  const currentBlocks = blocksRef.current;
                  const currentConnections = connectionsRef.current;
                  const currentSystems = systemsRef.current;
                  
                  // Add block and connections, then trigger system detection
                  const newBlocks = [...currentBlocks, pipe];
                  const allConnections = [...currentConnections, ...newConnections];
                  
                  console.log('🔍 Pipe-Draw: Detecting systems with:', {
                    blocks: newBlocks.length,
                    connections: allConnections.length,
                    systems: currentSystems.length
                  });
                  
                  const result = detectSystems(newBlocks, allConnections, currentSystems);
                  
                  console.log('✅ Pipe-Draw: System detection result:', {
                    blocks: result.blocks.length,
                    systems: result.systems.length
                  });
                  
                  setBlocks(result.blocks);
                  setConnections(allConnections);
                  setSystems(result.systems);
                  
                  toast.success(`Leitung DN${defaultDN} erstellt mit ${newConnections.length} Verbindung(en) (Länge: ${actualLength.toFixed(1)})`);
                } else {
                  // No connections - just add the pipe
                  handleBlockAdd(pipe);
                  toast.success(`Leitung DN${defaultDN} erstellt (Länge: ${actualLength.toFixed(1)})`);
                }
                
                // Reset for next pipe - keep tool active
                setPipeDrawStart(null);
                setPipeDrawPreview(null);
              } else {
                console.error('❌ Missing start point');
              }
            }}
          />
          )
        }
        rightSidebar={
          activePlanId ? (
           <PlanManagerPanel
              plans={plans}
              activePlanId={activePlanId}
              onSelectPlan={setActivePlanId}
              onCreatePlan={() => handleCreatePlan(activeDrawingId)}
              onDeletePlan={handleDeletePlan}
              onUpdatePlan={handleUpdatePlan}
              onDuplicatePlan={handleDuplicatePlan}
           />
        ) : (
        <SystemPropertiesPanel
          selectedBlocks={selectedBlocks}
          selectedSystem={selectedSystem}
          selectedConnections={selectedConnections}
          selectedLabels={placedLabels.filter(l => selectedLabelIds.includes(l.id))}
          allBlocks={blocks}
          allConnections={connections}
          allTextAnnotations={textAnnotations}
          allSystems={systems}
          pipeTypes={pipeTypes}
          onBlockUpdate={handleBlockUpdate}
          onSystemUpdate={handleSystemUpdate}
          onSystemAttributeAdd={handleSystemAttributeAdd}
          onSystemAttributeUpdate={handleSystemAttributeUpdate}
          onSystemAttributeDelete={handleSystemAttributeDelete}
          onConnectionAttributeAdd={handleConnectionAttributeAdd}
          onConnectionAttributeUpdate={handleConnectionAttributeUpdate}
          onConnectionAttributeDelete={handleConnectionAttributeDelete}
          onLabelEdit={handleLabelEdit}
          onLabelReassign={handleLabelReassign}
        />
          )
        }
      />

      {/* Status bar */}
      <div
        className="px-4 py-2 flex items-center justify-between text-xs"
        style={{
          backgroundColor: 'var(--md-surface-container-low)',
          color: 'var(--md-on-surface-variant)',
          borderTop: '1px solid var(--md-outline-variant)',
        }}
      >
        <div className="flex gap-4">
          <span>Blöcke: {blocks.length}</span>
          <span>Systeme: {systems.length}</span>
          <span>Verbindungen: {connections.length}</span>
          {selectedBlockIds.length > 0 && !reassigningLabelId && (
            <span style={{ color: 'var(--md-primary)' }}>
              {selectedBlockIds.length} ausgewählt (R zum Rotieren, Entf zum Löschen)
            </span>
          )}
          {reassigningLabelId && (
            <span style={{ color: 'var(--md-tertiary)', fontWeight: 'bold' }}>
              🔄 Neuzuweisung: Klicke auf Block oder Verbindung (ESC zum Abbrechen)
            </span>
          )}
        </div>
        <div>Zoom: {Math.round(viewState.zoom * 100)}%</div>
      </div>

      {/* Background Settings Dialog */}
      <BackgroundSettingsDialog
        open={showBackgroundSettings}
        onOpenChange={setShowBackgroundSettings}
        settings={backgroundSettings}
        onSettingsChange={setBackgroundSettings}
        onPreviewChange={setPreviewBackgroundSettings}
      />

      {/* View Settings Dialog */}
      <ViewSettingsDialog
        open={showViewSettings}
        onOpenChange={setShowViewSettings}
        settings={viewDisplaySettings}
        onSettingsChange={setViewDisplaySettings}
      />

      {/* Text Annotation Dialog */}
      <TextAnnotationDialog
        open={showTextDialog}
        onOpenChange={setShowTextDialog}
        annotation={editingTextAnnotation}
        onSave={handleTextAnnotationSave}
        systems={systems}
        pipeTypes={pipeTypes}
        projectName={projectName}
      />

      {/* Block Display Override Dialog */}
      {blockDisplayOverrideId && (
        <BlockDisplayOverrideDialog
          open={showBlockDisplayDialog}
          onOpenChange={setShowBlockDisplayDialog}
          blockName={blocks.find(b => b.id === blockDisplayOverrideId)?.blockData.name || ''}
          currentOverrides={blocks.find(b => b.id === blockDisplayOverrideId)?.viewDisplayOverrides}
          currentColorOverride={blocks.find(b => b.id === blockDisplayOverrideId)?.colorOverride}
          globalSettings={viewDisplaySettings}
          onSave={handleBlockDisplayOverrideSave}
        />
      )}

      {/* Navigation Drawer */}
      <NavigationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onNewFile={handleNewProject}
        onSave={handleSave}
        onSaveAs={handleSaveAs}
        onLoad={handleLoad}
        onExport={() => toast.info('Export - Feature kommt bald!')}
        onImport={handleLoad}
        onOpenLibrary={() => {
          setShowSidebar(true);
          setSidebarTab('bauteile');
          setDrawerOpen(false);
          toast.success(t('sidebar.parts.title') + ' geöffnet');
        }}
        onOpenHelp={() => toast.info('Hilfe - Feature kommt bald!')}
        onOpenSettings={() => {
          setShowBackgroundSettings(true);
          setDrawerOpen(false);
        }}
      />

      {/* Pipe Create Dialog */}
      <PipeCreateDialog
        open={showPipeDialog}
        onOpenChange={(open) => {
          setShowPipeDialog(open);
          if (!open) {
            // Reset tool states when dialog closes
            setPipeDrawStart(null);
            setPipeDrawPreview(null);
            setActiveTool('select');
          }
        }}
        startPoint={pipeDrawStart || undefined}
        endPoint={pipeDrawPreview || undefined}
        onPipeCreate={(pipe) => {
          handleBlockAdd(pipe);
          // Reset tool states after creating pipe
          setPipeDrawStart(null);
          setPipeDrawPreview(null);
          setActiveTool('select');
        }}
      />

      {/* Dimension Edit Dialog */}
      <DimensionEditDialog
        open={showDimensionDialog}
        onOpenChange={setShowDimensionDialog}
        dimension={selectedDimension?.dimension || null}
        blockId={selectedDimension?.blockId || null}
        onDimensionUpdate={handleDimensionUpdate}
      />

      {/* Label Edit Dialog */}
      <LabelEditDialog
        open={showLabelEditDialog}
        onOpenChange={setShowLabelEditDialog}
        label={editingLabel}
        template={editingLabel ? labelTemplates.find(t => t.id === editingLabel.templateId) || null : null}
        blocks={blocks}
        connections={connections}
        textAnnotations={textAnnotations}
        onSave={handleLabelEditSave}
      />

      {/* Parameter Management Dialog */}
      <ParameterManagementDialog
        open={showParameterManagement}
        onOpenChange={setShowParameterManagement}
        blocks={blocks}
        connections={connections}
        systems={systems}
        onBlockAttributeUpdate={handleBlockAttributeUpdate}
        onBlockAttributeDelete={handleBlockAttributeDelete}
        onConnectionAttributeUpdate={handleConnectionAttributeUpdate}
        onConnectionAttributeDelete={handleConnectionAttributeDelete}
        onSystemAttributeUpdate={handleSystemAttributeUpdate}
        onSystemAttributeDelete={handleSystemAttributeDelete}
      />
    </div>
  );
}